/**
 * Metal Asia localization catalog.
 *
 * Product names, product codes, registered brand names, standards, dimensions,
 * units, and verified technical values belong to the language-independent
 * product model. They must never be translated through this catalog.
 */

import { publicSiteConfig } from "../company/public-config";

export const locales = publicSiteConfig.supportedLocales;
export const legacyLocales = locales;

export type Locale = (typeof locales)[number];
export type LegacyLocale = (typeof legacyLocales)[number];

export const defaultLocale: Locale = "en";

export interface LanguageMeta {
  nativeName: string;
  htmlLang: string;
  searchHreflang: string;
  direction: "ltr" | "rtl";
}

export const languageMeta = {
  en: { nativeName: "English", htmlLang: "en", searchHreflang: "en", direction: "ltr" },
  tr: { nativeName: "Türkçe", htmlLang: "tr", searchHreflang: "tr-TR", direction: "ltr" },
  ru: { nativeName: "Русский", htmlLang: "ru", searchHreflang: "ru-RU", direction: "ltr" },
  kk: { nativeName: "Қазақша", htmlLang: "kk", searchHreflang: "kk-KZ", direction: "ltr" },
  ky: { nativeName: "Кыргызча", htmlLang: "ky", searchHreflang: "ky-KG", direction: "ltr" },
  uz: { nativeName: "O‘zbekcha", htmlLang: "uz", searchHreflang: "uz-UZ", direction: "ltr" },
  tg: { nativeName: "Тоҷикӣ", htmlLang: "tg", searchHreflang: "tg-TJ", direction: "ltr" },
  tk: { nativeName: "Türkmençe", htmlLang: "tk", searchHreflang: "tk-TM", direction: "ltr" },
} as const satisfies Record<Locale, LanguageMeta>;

export const routeKeys = [
  "about",
  "products",
  "industries",
  "documents",
  "quality",
  "services",
  "contact",
  "privacy",
  "cookies",
  "family",
] as const;

export type RouteKey = (typeof routeKeys)[number];

/**
 * Route-safe localized segments. The canonical product slug remains identical
 * across languages and is appended after the localized products segment.
 */
export const localizedRoutes = {
  en: {
    about: "about",
    products: "products",
    industries: "industries",
    documents: "technical-documents",
    quality: "quality-and-standards",
    services: "technical-support",
    contact: "contact",
    privacy: "privacy-policy",
    cookies: "cookie-policy",
    family: "family",
  },
  tr: {
    about: "hakkimizda",
    products: "urunler",
    industries: "sektorler",
    documents: "teknik-dokumanlar",
    quality: "kalite-ve-standartlar",
    services: "teknik-destek",
    contact: "iletisim",
    privacy: "gizlilik-politikasi",
    cookies: "cerez-politikasi",
    family: "aile",
  },
  ru: {
    about: "o-kompanii",
    products: "produktsiya",
    industries: "otrasli",
    documents: "tekhnicheskie-dokumenty",
    quality: "kachestvo-i-standarty",
    services: "tekhnicheskaya-podderzhka",
    contact: "kontakty",
    privacy: "politika-konfidentsialnosti",
    cookies: "politika-faylov-cookie",
    family: "semeystvo",
  },
  kk: {
    about: "kompaniya-turaly",
    products: "onimder",
    industries: "salalar",
    documents: "tekhnikalyk-kuzhattar",
    quality: "sapa-zhane-standarttar",
    services: "tekhnikalyk-koldau",
    contact: "baylanys",
    privacy: "kupiyalyk-sayasaty",
    cookies: "cookie-sayasaty",
    family: "onim-otbasy",
  },
  ky: {
    about: "kompaniya-zhonundo",
    products: "onumdor",
    industries: "tarmaktar",
    documents: "tekhnikalyk-dokumentter",
    quality: "sapat-zhana-standarttar",
    services: "tekhnikalyk-koldoo",
    contact: "baylanysh",
    privacy: "kupuyaluk-sayasaty",
    cookies: "cookie-sayasaty",
    family: "onum-buloso",
  },
  uz: {
    about: "kompaniya-haqida",
    products: "mahsulotlar",
    industries: "tarmoqlar",
    documents: "texnik-hujjatlar",
    quality: "sifat-va-standartlar",
    services: "texnik-yordam",
    contact: "aloqa",
    privacy: "maxfiylik-siyosati",
    cookies: "cookie-siyosati",
    family: "mahsulot-oilasi",
  },
  tg: {
    about: "dar-borai-shirkat",
    products: "mahsulot",
    industries: "sohaho",
    documents: "hujjathoi-tekhniky",
    quality: "sifat-va-standartho",
    services: "dastgirii-tekhniky",
    contact: "tamos",
    privacy: "siyosati-mahfiyat",
    cookies: "siyosati-cookie",
    family: "oilai-mahsulot",
  },
  tk: {
    about: "kompaniya-barada",
    products: "onumler",
    industries: "pudaklar",
    documents: "tehniki-resminamalar",
    quality: "hil-we-standartlar",
    services: "tehniki-goldaw",
    contact: "habarlasmak",
    privacy: "gizlinlik-syyasaty",
    cookies: "cookie-syyasaty",
    family: "onum-masgalasy",
  },
} as const satisfies Record<Locale, Record<RouteKey, string>>;

export function extendLocaleRecord<T>(
  legacy: Record<LegacyLocale, T>,
): Record<Locale, T> {
  return Object.fromEntries(
    locales.map((locale) => [locale, legacy[locale]]),
  ) as Record<Locale, T>;
}

export const preservedBrandNames = [
  "METAL ASIA",
  "BISALLOY®",
  "BISALLOY® Armour",
] as const;

export type IndustryKey =
  | "defense"
  | "mining"
  | "mineralProcessing"
  | "construction"
  | "transportation"
  | "energy"
  | "agriculture"
  | "engineering";

export interface TranslationMessages {
  common: {
    companyName: string;
    exploreProducts: string;
    requestQuote: string;
    requestTechnicalInfo: string;
    learnMore: string;
    viewAll: string;
    download: string;
    openInNewTab: string;
    search: string;
    clear: string;
    close: string;
    back: string;
    next: string;
    previous: string;
    loading: string;
    unknown: string;
    notAvailable: string;
    language: string;
    menu: string;
  };
  nav: {
    home: string;
    about: string;
    products: string;
    industries: string;
    documents: string;
    quality: string;
    services: string;
    contact: string;
    wear: string;
  };
  home: {
    eyebrow: string;
    title: string;
    intro: string;
    primaryCta: string;
    secondaryCta: string;
    familiesTitle: string;
    familiesIntro: string;
    featuredTitle: string;
    featuredIntro: string;
    industriesTitle: string;
    industriesIntro: string;
    supportTitle: string;
    supportText: string;
    qualityTitle: string;
    qualityText: string;
    documentsTitle: string;
    documentsText: string;
    aboutTitle: string;
    aboutText: string;
    contactTitle: string;
    contactText: string;
  };
  about: {
    title: string;
    intro: string;
    positioningTitle: string;
    positioningText: string;
    principlesTitle: string;
    accuracyTitle: string;
    accuracyText: string;
    traceabilityTitle: string;
    traceabilityText: string;
    supportTitle: string;
    supportText: string;
    roleDisclosure: string;
  };
  products: {
    title: string;
    intro: string;
    searchLabel: string;
    searchPlaceholder: string;
    resultCount: string;
    familyFilter: string;
    applicationFilter: string;
    hardnessFilter: string;
    thicknessFilter: string;
    yieldStrengthFilter: string;
    tensileStrengthFilter: string;
    viewLabel: string;
    gridView: string;
    listView: string;
    activeFilters: string;
    clearFilters: string;
    compare: string;
    addToCompare: string;
    removeFromCompare: string;
    compareTitle: string;
    compareHint: string;
    compareMinimum: string;
    compareMaximum: string;
    compareNow: string;
    emptyTitle: string;
    emptyText: string;
    missingNumericData: string;
    viewDetails: string;
    requestDocument: string;
    downloadDatasheet: string;
  };
  product: {
    overview: string;
    gallery: string;
    advantages: string;
    applications: string;
    technicalSpecifications: string;
    thicknessRange: string;
    brinellHardness: string;
    yieldStrength: string;
    proofStrength: string;
    tensileStrength: string;
    chemicalComposition: string;
    mechanicalProperties: string;
    equivalentStandards: string;
    certifications: string;
    processingNotes: string;
    cutting: string;
    welding: string;
    bending: string;
    relatedProducts: string;
    documentModule: string;
    dataSource: string;
    lastVerified: string;
    missingValue: string;
    missingDataNotice: string;
    noDocumentTitle: string;
    noDocumentText: string;
    requestDocument: string;
    documentAvailableIn: string;
    selectedLanguageUnavailable: string;
  };
  industries: {
    title: string;
    intro: string;
    exploreSector: string;
    sectors: Record<IndustryKey, { title: string; description: string }>;
  };
  documents: {
    title: string;
    intro: string;
    searchLabel: string;
    searchPlaceholder: string;
    familyFilter: string;
    productFilter: string;
    typeFilter: string;
    languageFilter: string;
    yearFilter: string;
    datasheet: string;
    technicalGuide: string;
    brochure: string;
    certificate: string;
    documentLanguage: string;
    version: string;
    publicationDate: string;
    fileSize: string;
    lastUpdated: string;
    source: string;
    familyDatasheet: string;
    selectedLanguageMissing: string;
    preview: string;
    previewUnavailable: string;
    emptyTitle: string;
    emptyText: string;
  };
  quality: {
    title: string;
    intro: string;
    verifiedOnlyTitle: string;
    verifiedOnlyText: string;
    certificatesTitle: string;
    noCertificatesTitle: string;
    noCertificatesText: string;
    standardsTitle: string;
    traceabilityTitle: string;
    traceabilityText: string;
  };
  services: {
    title: string;
    intro: string;
    selectionTitle: string;
    selectionText: string;
    documentsTitle: string;
    documentsText: string;
    processingTitle: string;
    processingText: string;
    enquiryTitle: string;
    enquiryText: string;
  };
  contact: {
    title: string;
    intro: string;
    detailsTitle: string;
    detailsUnavailable: string;
    quoteTitle: string;
    quoteText: string;
  };
  form: {
    fullName: string;
    company: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
    product: string;
    thickness: string;
    quantity: string;
    message: string;
    privacyConsent: string;
    privacyLinkText: string;
    selectPlaceholder: string;
    optional: string;
    submit: string;
    submitting: string;
    required: string;
    invalidEmail: string;
    invalidPhone: string;
    errorSummaryTitle: string;
    spamDetected: string;
    successTitle: string;
    successText: string;
    errorTitle: string;
    errorText: string;
    integrationMissingTitle: string;
    integrationMissingText: string;
  };
  legal: {
    privacyTitle: string;
    privacyBlockerTitle: string;
    privacyBlockerText: string;
    cookiesTitle: string;
    cookiesBlockerTitle: string;
    cookiesBlockerText: string;
    preferenceStorageText: string;
    legalReviewRequired: string;
  };
  breadcrumbs: {
    label: string;
    home: string;
    currentPage: string;
  };
  notFound: {
    title: string;
    text: string;
    backHome: string;
    browseProducts: string;
  };
  footer: {
    tagline: string;
    productLinksTitle: string;
    companyLinksTitle: string;
    resourceLinksTitle: string;
    sitemapLabel: string;
    contactTitle: string;
    rights: string;
    legalEntityMissing: string;
    contactDetailsMissing: string;
  };
  seo: {
    home: { title: string; description: string };
    about: { title: string; description: string };
    products: { title: string; description: string };
    industries: { title: string; description: string };
    documents: { title: string; description: string };
    quality: { title: string; description: string };
    services: { title: string; description: string };
    contact: { title: string; description: string };
    privacy: { title: string; description: string };
    cookies: { title: string; description: string };
    productDetail: { titleTemplate: string; descriptionTemplate: string };
    industryDetail: { titleTemplate: string; descriptionTemplate: string };
  };
  accessibility: {
    skipToContent: string;
    mainNavigation: string;
    languageSelector: string;
    selectLanguage: string;
    openMenu: string;
    closeMenu: string;
    productSearch: string;
    filters: string;
    removeFilter: string;
    gridView: string;
    listView: string;
    compareSelection: string;
    documentPreview: string;
    previousImage: string;
    nextImage: string;
    closeDialog: string;
    externalLink: string;
    opensNewTab: string;
    requiredField: string;
    errorSummary: string;
    loading: string;
  };
}

export type TechnicalGlossaryTerm =
  | "abrasionResistantSteel"
  | "highStrengthSteel"
  | "armourSteel"
  | "protectionSteel"
  | "steelPlate"
  | "brinellHardness"
  | "yieldStrength"
  | "proofStrength"
  | "tensileStrength"
  | "thicknessRange"
  | "chemicalComposition"
  | "mechanicalProperties"
  | "heatTreatment"
  | "cutting"
  | "welding"
  | "bending"
  | "datasheet"
  | "technicalGuide"
  | "certificate"
  | "traceability"
  | "applicationArea"
  | "pressureVessel"
  | "ballisticProtection"
  | "wearResistance"
  | "productFamily";

const legacyTechnicalGlossary = {
  abrasionResistantSteel: {
    en: "abrasion-resistant steel",
    tr: "aşınmaya dayanıklı çelik",
    ru: "износостойкая сталь",
    kk: "тозуға төзімді болат",
    ky: "эскирүүгө туруктуу болот",
    uz: "yeyilishga chidamli po‘lat",
    tg: "пӯлоди ба фарсудашавӣ тобовар",
    tk: "könelmä çydamly polat",
  },
  highStrengthSteel: {
    en: "high-strength steel",
    tr: "yüksek dayanımlı çelik",
    ru: "высокопрочная сталь",
    kk: "беріктігі жоғары болат",
    ky: "жогорку бекемдиктеги болот",
    uz: "yuqori mustahkam po‘lat",
    tg: "пӯлоди мустаҳкамаш баланд",
    tk: "ýokary berklikli polat",
  },
  armourSteel: {
    en: "armour steel",
    tr: "zırh çeliği",
    ru: "броневая сталь",
    kk: "сауыттық болат",
    ky: "соот болоту",
    uz: "zirhli po‘lat",
    tg: "пӯлоди зиреҳӣ",
    tk: "sowut polady",
  },
  protectionSteel: {
    en: "protection steel",
    tr: "koruma çeliği",
    ru: "защитная сталь",
    kk: "қорғаныш болаты",
    ky: "коргоочу болот",
    uz: "himoya po‘lati",
    tg: "пӯлоди муҳофизатӣ",
    tk: "gorag polady",
  },
  steelPlate: {
    en: "steel plate",
    tr: "çelik levha",
    ru: "стальной лист",
    kk: "болат табақ",
    ky: "болот табак",
    uz: "po‘lat list",
    tg: "варақи пӯлодӣ",
    tk: "polat list",
  },
  brinellHardness: {
    en: "Brinell hardness",
    tr: "Brinell sertliği",
    ru: "твёрдость по Бринеллю",
    kk: "Бринелль бойынша қаттылық",
    ky: "Бринелль боюнча катуулук",
    uz: "Brinell bo‘yicha qattiqlik",
    tg: "сахтӣ аз рӯи Бринелл",
    tk: "Brinell boýunça gatylyk",
  },
  yieldStrength: {
    en: "yield strength",
    tr: "akma dayanımı",
    ru: "предел текучести",
    kk: "аққыштық шегі",
    ky: "агуу чеги",
    uz: "oquvchanlik chegarasi",
    tg: "ҳадди ҷорӣшавӣ",
    tk: "akma berkligi",
  },
  proofStrength: {
    en: "proof strength",
    tr: "kanıt dayanımı",
    ru: "условный предел текучести",
    kk: "шартты аққыштық шегі",
    ky: "шарттуу агуу чеги",
    uz: "shartli oquvchanlik chegarasi",
    tg: "ҳадди шартии ҷорӣшавӣ",
    tk: "şertli akma berkligi",
  },
  tensileStrength: {
    en: "tensile strength",
    tr: "çekme dayanımı",
    ru: "предел прочности при растяжении",
    kk: "созылу беріктігі",
    ky: "созулуудагы бекемдик",
    uz: "cho‘zilishdagi mustahkamlik",
    tg: "мустаҳкамӣ ҳангоми кашиш",
    tk: "çekilme berkligi",
  },
  thicknessRange: {
    en: "thickness range",
    tr: "kalınlık aralığı",
    ru: "диапазон толщины",
    kk: "қалыңдық ауқымы",
    ky: "калыңдык аралыгы",
    uz: "qalinlik oralig‘i",
    tg: "доираи ғафсӣ",
    tk: "galyňlyk aralygy",
  },
  chemicalComposition: {
    en: "chemical composition",
    tr: "kimyasal bileşim",
    ru: "химический состав",
    kk: "химиялық құрам",
    ky: "химиялык курам",
    uz: "kimyoviy tarkib",
    tg: "таркиби кимиёвӣ",
    tk: "himiki düzümi",
  },
  mechanicalProperties: {
    en: "mechanical properties",
    tr: "mekanik özellikler",
    ru: "механические свойства",
    kk: "механикалық қасиеттер",
    ky: "механикалык касиеттер",
    uz: "mexanik xususiyatlar",
    tg: "хосиятҳои механикӣ",
    tk: "mehaniki häsiýetler",
  },
  heatTreatment: {
    en: "heat treatment",
    tr: "ısıl işlem",
    ru: "термическая обработка",
    kk: "термиялық өңдеу",
    ky: "термиялык иштетүү",
    uz: "termik ishlov",
    tg: "коркарди гармӣ",
    tk: "ýylylyk bilen işleme",
  },
  cutting: {
    en: "cutting",
    tr: "kesim",
    ru: "резка",
    kk: "кесу",
    ky: "кесүү",
    uz: "kesish",
    tg: "буридан",
    tk: "kesmek",
  },
  welding: {
    en: "welding",
    tr: "kaynak",
    ru: "сварка",
    kk: "дәнекерлеу",
    ky: "ширетүү",
    uz: "payvandlash",
    tg: "кафшеркунӣ",
    tk: "kebşirlemek",
  },
  bending: {
    en: "bending",
    tr: "büküm",
    ru: "гибка",
    kk: "ию",
    ky: "ийүү",
    uz: "bukish",
    tg: "хамкунӣ",
    tk: "eğmek",
  },
  datasheet: {
    en: "datasheet",
    tr: "teknik veri föyü",
    ru: "технический паспорт",
    kk: "техникалық деректер парағы",
    ky: "техникалык маалымат барагы",
    uz: "texnik ma’lumotlar varag‘i",
    tg: "варақаи маълумоти техникӣ",
    tk: "tehniki maglumat sahypasy",
  },
  technicalGuide: {
    en: "technical guide",
    tr: "teknik kılavuz",
    ru: "техническое руководство",
    kk: "техникалық нұсқаулық",
    ky: "техникалык колдонмо",
    uz: "texnik qo‘llanma",
    tg: "дастури техникӣ",
    tk: "tehniki gollanma",
  },
  certificate: {
    en: "certificate",
    tr: "sertifika",
    ru: "сертификат",
    kk: "сертификат",
    ky: "сертификат",
    uz: "sertifikat",
    tg: "шаҳодатнома",
    tk: "şahadatnama",
  },
  traceability: {
    en: "traceability",
    tr: "izlenebilirlik",
    ru: "прослеживаемость",
    kk: "қадағаланымдылық",
    ky: "көзөмөлдөнүүчүлүк",
    uz: "kuzatuvchanlik",
    tg: "пайгиришавандагӣ",
    tk: "yzarlap bolujylyk",
  },
  applicationArea: {
    en: "application area",
    tr: "kullanım alanı",
    ru: "область применения",
    kk: "қолдану саласы",
    ky: "колдонуу тармагы",
    uz: "qo‘llanish sohasi",
    tg: "соҳаи истифода",
    tk: "ulanyş pudagy",
  },
  pressureVessel: {
    en: "pressure vessel",
    tr: "basınçlı kap",
    ru: "сосуд под давлением",
    kk: "қысымды ыдыс",
    ky: "басым алдында иштеген идиш",
    uz: "bosim ostidagi idish",
    tg: "зарфи таҳти фишор",
    tk: "basyşly gap",
  },
  ballisticProtection: {
    en: "ballistic protection",
    tr: "balistik koruma",
    ru: "баллистическая защита",
    kk: "баллистикалық қорғаныс",
    ky: "баллистикалык коргоо",
    uz: "ballistik himoya",
    tg: "муҳофизати баллистикӣ",
    tk: "ballistik gorag",
  },
  wearResistance: {
    en: "wear resistance",
    tr: "aşınma direnci",
    ru: "износостойкость",
    kk: "тозуға төзімділік",
    ky: "эскирүүгө туруктуулук",
    uz: "yeyilishga chidamlilik",
    tg: "тобоварӣ ба фарсудашавӣ",
    tk: "könelmä çydamlylyk",
  },
  productFamily: {
    en: "product family",
    tr: "ürün ailesi",
    ru: "семейство продукции",
    kk: "өнім тобы",
    ky: "өнүмдөр тобу",
    uz: "mahsulot oilasi",
    tg: "оилаи маҳсулот",
    tk: "önüm maşgalasy",
  },
} as const satisfies Record<
  TechnicalGlossaryTerm,
  Record<LegacyLocale, string>
>;

export const technicalGlossary = Object.fromEntries(
  Object.entries(legacyTechnicalGlossary).map(([term, translations]) => [
    term,
    extendLocaleRecord(translations),
  ]),
) as Record<TechnicalGlossaryTerm, Record<Locale, string>>;

export type TranslationReviewScope =
  | "technicalTerminology"
  | "legalContent"
  | "seoCopy"
  | "interfaceCopy";

export interface TranslationReviewStatus {
  humanReviewRequired: boolean;
  scopes: readonly TranslationReviewScope[];
}

/**
 * A `true` status means the locale is complete in the UI but still requires a
 * native-speaking metallurgy/legal review before production publication.
 */
export const translationReviewStatus = {
  en: { humanReviewRequired: false, scopes: [] },
  tr: { humanReviewRequired: false, scopes: [] },
  ru: { humanReviewRequired: false, scopes: [] },
  kk: {
    humanReviewRequired: true,
    scopes: ["technicalTerminology", "legalContent", "seoCopy", "interfaceCopy"],
  },
  ky: {
    humanReviewRequired: true,
    scopes: ["technicalTerminology", "legalContent", "seoCopy", "interfaceCopy"],
  },
  uz: {
    humanReviewRequired: true,
    scopes: ["technicalTerminology", "legalContent", "seoCopy", "interfaceCopy"],
  },
  tg: {
    humanReviewRequired: true,
    scopes: ["technicalTerminology", "legalContent", "seoCopy", "interfaceCopy"],
  },
  tk: {
    humanReviewRequired: true,
    scopes: ["technicalTerminology", "legalContent", "seoCopy", "interfaceCopy"],
  },
} as const satisfies Record<Locale, TranslationReviewStatus>;

const legacyMessages = {
  en: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Explore products",
      requestQuote: "Request a quote",
      requestTechnicalInfo: "Request technical information",
      learnMore: "Learn more",
      viewAll: "View all",
      download: "Download",
      openInNewTab: "Open in a new tab",
      search: "Search",
      clear: "Clear",
      close: "Close",
      back: "Back",
      next: "Next",
      previous: "Previous",
      loading: "Loading",
      unknown: "Unknown",
      notAvailable: "Not available",
      language: "Language",
      menu: "Menu",
    },
    nav: {
      home: "Home",
      about: "About us",
      products: "Products",
      industries: "Industries",
      documents: "Technical documents",
      quality: "Quality and standards",
      services: "Technical support",
      contact: "Contact",
      wear: "Wear comparison",
    },
    home: {
      eyebrow: "Steel plate, metal sheet and high-performance steels",
      title: "Verified steel information for demanding applications",
      intro:
        "Metal Asia organises wear plate, high-strength structural steel, armour and protection steel products in one catalogue with available verified values and clearly linked sources.",
      primaryCta: "Explore products",
      secondaryCta: "Request a quote",
      familiesTitle: "Product families",
      familiesIntro:
        "Find products by steel family, intended application and available verified properties.",
      featuredTitle: "Featured products",
      featuredIntro:
        "Review selected grades and open their source-linked technical information.",
      industriesTitle: "Industries and applications",
      industriesIntro:
        "Navigate steel options by operating environment and engineering requirement.",
      supportTitle: "Technical support and services",
      supportText:
        "Request help with product selection, document access and application questions.",
      qualityTitle: "Quality, certificates and standards",
      qualityText:
        "Only standards and certificates supported by approved source documents are displayed.",
      documentsTitle: "Technical document centre",
      documentsText:
        "Search available datasheets, technical guides, brochures and certificates by product and language.",
      aboutTitle: "About Metal Asia",
      aboutText:
        "METAL ASIA supplies iron and steel products as an official distributor of BISALLOY®.",
      contactTitle: "Discuss your requirement",
      contactText:
        "Share the product, thickness, quantity and application details needed for a qualified response.",
    },
    about: {
      title: "About Metal Asia",
      intro:
        "METAL ASIA sells and supplies iron and steel products. We are also an official distributor of BISALLOY®.",
      positioningTitle: "Source before product claim",
      positioningText:
        "Every published technical value is shown with its source; fields not supported by an official document are not estimated. This makes wear, strength and protection requirements easier to compare across product families.",
      principlesTitle: "Our information principles",
      accuracyTitle: "Accuracy",
      accuracyText:
        "Technical values are published only when they can be traced to an approved source.",
      traceabilityTitle: "Traceability",
      traceabilityText:
        "Product records identify their supporting document and latest verification status.",
      supportTitle: "Relevant support",
      supportText:
        "Enquiries capture the application details needed to route a technical or commercial request.",
      roleDisclosure:
        "METAL ASIA sells and supplies iron and steel products and offers BISALLOY® products as an official distributor.",
    },
    products: {
      title: "All products",
      intro:
        "Search, filter and compare specialist steel grades without treating unavailable technical values as zero.",
      searchLabel: "Search products",
      searchPlaceholder: "Search by product, family or application",
      resultCount: "{count} products",
      familyFilter: "Product family",
      applicationFilter: "Application",
      hardnessFilter: "Brinell hardness",
      thicknessFilter: "Thickness",
      yieldStrengthFilter: "Yield strength",
      tensileStrengthFilter: "Tensile strength",
      viewLabel: "Results view",
      gridView: "Card view",
      listView: "List view",
      activeFilters: "Active filters",
      clearFilters: "Clear all filters",
      compare: "Compare",
      addToCompare: "Add to comparison",
      removeFromCompare: "Remove from comparison",
      compareTitle: "Product comparison",
      compareHint: "Select between two and four products.",
      compareMinimum: "Select at least two products to compare.",
      compareMaximum: "You can compare up to four products.",
      compareNow: "Compare selected products",
      emptyTitle: "No matching products",
      emptyText: "Adjust your search or clear one or more filters.",
      missingNumericData:
        "Products without a verified value are excluded from this numeric filter; missing data is not treated as zero.",
      viewDetails: "View details",
      requestDocument: "Request technical document",
      downloadDatasheet: "Download datasheet",
    },
    product: {
      overview: "Overview",
      gallery: "Product gallery",
      advantages: "Key advantages",
      applications: "Applications",
      technicalSpecifications: "Technical specifications",
      thicknessRange: "Thickness range",
      brinellHardness: "Brinell hardness",
      yieldStrength: "Yield strength",
      proofStrength: "Proof strength",
      tensileStrength: "Tensile strength",
      chemicalComposition: "Chemical composition",
      mechanicalProperties: "Mechanical properties",
      equivalentStandards: "Equivalent standards",
      certifications: "Verified certificates",
      processingNotes: "Cutting, welding and bending notes",
      cutting: "Cutting",
      welding: "Welding",
      bending: "Bending",
      relatedProducts: "Related products",
      documentModule: "Datasheets and supporting documents",
      dataSource: "Source document",
      lastVerified: "Last verified",
      missingValue: "No verified value available",
      missingDataNotice:
        "Only fields supported by official documents are shown. Unreported values have not been estimated.",
      noDocumentTitle: "Technical document not available",
      noDocumentText:
        "No approved document is currently linked to this product. You can request it from the technical team.",
      requestDocument: "Request this document",
      documentAvailableIn: "This document is available in {language}.",
      selectedLanguageUnavailable:
        "No technical document is available in the selected language. Existing documents remain listed with their language.",
    },
    industries: {
      title: "Industries",
      intro:
        "Explore Metal Asia product families in the context of wear, load-bearing and protection requirements. Final grade and thickness selection must be confirmed against verified technical requirements.",
      exploreSector: "Explore this industry",
      sectors: {
        defense: {
          title: "Defence and security",
          description:
            "Review documented armour and protection steel grades for ballistic and protective engineering evaluations.",
        },
        mining: {
          title: "Mining and excavation",
          description:
            "Explore abrasion-resistant and high-strength grades for wear-intensive extraction and earthmoving applications.",
        },
        mineralProcessing: {
          title: "Mineral processing",
          description:
            "Find documented options for chutes, liners, hoppers and other components exposed to abrasive material flow.",
        },
        construction: {
          title: "Construction",
          description:
            "Compare high-strength and wear-resistant grades for demanding structural and site equipment requirements.",
        },
        transportation: {
          title: "Transportation",
          description:
            "Assess steel grades for weight-sensitive, load-bearing and wear-exposed transport applications.",
        },
        energy: {
          title: "Energy",
          description:
            "Navigate verified steel information for equipment and infrastructure operating under demanding service conditions.",
        },
        agriculture: {
          title: "Agriculture",
          description:
            "Explore wear-resistant grades for soil-engaging and material-handling agricultural components.",
        },
        engineering: {
          title: "General engineering and fabrication",
          description:
            "Compare documented properties for engineered components, machinery and fabrication planning.",
        },
      },
    },
    documents: {
      title: "Technical document centre",
      intro:
        "Find available approved documents in the Metal Asia Technical Document Centre by product, family, type, language and publication year. Products without a document are clearly identified.",
      searchLabel: "Search documents",
      searchPlaceholder: "Search by document or product name",
      familyFilter: "Product family",
      productFilter: "Product",
      typeFilter: "Document type",
      languageFilter: "Document language",
      yearFilter: "Publication year",
      datasheet: "Datasheet",
      technicalGuide: "Technical guide",
      brochure: "Brochure",
      certificate: "Certificate",
      documentLanguage: "Document language",
      version: "Version",
      publicationDate: "Publication date",
      fileSize: "File size",
      lastUpdated: "Last updated",
      source: "Source",
      familyDatasheet: "Product-family datasheet",
      selectedLanguageMissing:
        "No document is available in the selected interface language; available documents are shown with their actual language.",
      preview: "Preview document",
      previewUnavailable:
        "Preview is not supported on this device. Open the document in a new tab or download it.",
      emptyTitle: "No matching documents",
      emptyText: "Adjust your search or clear one or more document filters.",
    },
    quality: {
      title: "Quality, certificates and standards",
      intro:
        "Metal Asia displays standards and certificate information only when an approved source and verified product relationship are available. Missing documentation is stated clearly.",
      verifiedOnlyTitle: "Verified information only",
      verifiedOnlyText:
        "A standard or certificate is shown only when its source and product relationship have been confirmed.",
      certificatesTitle: "Certificates",
      noCertificatesTitle: "No approved certificates published",
      noCertificatesText:
        "Certificates will appear here after the relevant files and their scope have been verified.",
      standardsTitle: "Standards",
      traceabilityTitle: "Document traceability",
      traceabilityText:
        "Published records retain their source, language, version, publication date and verification status when known.",
    },
    services: {
      title: "Technical support and services",
      intro:
        "Metal Asia provides a structured enquiry path for product selection, document access and clarification of application-specific requirements. Cutting, welding and bending information is based only on official product documents.",
      selectionTitle: "Product selection support",
      selectionText:
        "Compare documented properties and share your application requirements for further review.",
      documentsTitle: "Document support",
      documentsText:
        "Request an unavailable datasheet or ask about the language and revision of an existing document.",
      processingTitle: "Processing information",
      processingText:
        "Cutting, welding and bending guidance is provided only where supported by official product documentation.",
      enquiryTitle: "Technical enquiry",
      enquiryText:
        "Include grade, thickness, quantity and operating conditions so the request can be assessed accurately.",
    },
    contact: {
      title: "Contact and quote request",
      intro:
        "Tell us which product and technical requirement you are evaluating.",
      detailsTitle: "Contact details",
      detailsUnavailable:
        "Verified email, phone, WhatsApp and address details have not yet been configured for publication.",
      quoteTitle: "Request a quote",
      quoteText:
        "Provide product, thickness, quantity and application information for a qualified response.",
    },
    form: {
      fullName: "Full name",
      company: "Company",
      email: "Email",
      phone: "Phone",
      country: "Country",
      city: "City",
      address: "Address",
      product: "Product of interest",
      thickness: "Required thickness",
      quantity: "Quantity",
      message: "Message",
      privacyConsent: "I have read the privacy notice about how my enquiry is processed.",
      privacyLinkText: "Read the privacy policy",
      selectPlaceholder: "Select an option",
      optional: "Optional",
      submit: "Send request",
      submitting: "Sending request",
      required: "This field is required.",
      invalidEmail: "Enter a valid email address.",
      invalidPhone: "Enter a valid phone number.",
      errorSummaryTitle: "Please correct the following fields",
      spamDetected: "The request could not be verified. Please try again.",
      successTitle: "Your request has been received",
      successText:
        "The configured service confirmed receipt of your request. Keep the reference number shown for follow-up.",
      errorTitle: "Your request was not sent",
      errorText:
        "The service could not accept your request. Review the fields and try again later.",
      integrationMissingTitle: "Email delivery will be used",
      integrationMissingText:
        "Server delivery is not connected yet. After validation, the request opens as a prepared message in your email application and is sent only when you approve it there.",
    },
    legal: {
      privacyTitle: "Privacy policy",
      privacyBlockerTitle: "Approved privacy text required",
      privacyBlockerText:
        "The privacy policy cannot be published until the legal entity, data controller, processing purposes, retention periods and contact details are approved.",
      cookiesTitle: "Cookie policy",
      cookiesBlockerTitle: "Approved cookie text required",
      cookiesBlockerText:
        "The cookie policy cannot be published until the cookies in use, purposes, durations, providers and consent process are approved.",
      preferenceStorageText:
        "Essential language-preference storage is kept separate from analytics and marketing consent.",
      legalReviewRequired: "Legal review is required before publication.",
    },
    breadcrumbs: {
      label: "Breadcrumb",
      home: "Home",
      currentPage: "Current page",
    },
    notFound: {
      title: "Page not found",
      text:
        "The address may have changed, or this page may not be available in the selected language.",
      backHome: "Return to home",
      browseProducts: "Browse all products",
    },
    footer: {
      tagline:
        "Verified product information and technical document access for wear plate and high-performance steels.",
      productLinksTitle: "Products",
      companyLinksTitle: "Company",
      resourceLinksTitle: "Resources",
      sitemapLabel: "XML sitemap",
      contactTitle: "Contact",
      rights: "All rights reserved.",
      legalEntityMissing: "Verified legal entity details pending publication.",
      contactDetailsMissing: "Verified contact details pending publication.",
    },
    seo: {
      home: {
        title: "Steel & Metal Plates | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA supplies BISALLOY® steel plate, metal sheet, wear-resistant and abrasion-resistant steel plate, high-strength structural steel, armour and protection steel.",
      },
      about: {
        title: "About Metal Asia",
        description:
          "METAL ASIA is a seller and supplier of iron and steel products and an official distributor of BISALLOY®.",
      },
      products: {
        title: "Steel Plate & Wear-Resistant Steel | METAL ASIA",
        description:
          "Search, filter and compare high-strength, wear-resistant, armour and protection steel grades.",
      },
      industries: {
        title: "Industries and Applications | Metal Asia",
        description:
          "Explore documented specialist steel options by industry, operating environment and engineering requirement.",
      },
      documents: {
        title: "Technical Documents | Metal Asia",
        description:
          "Search available steel datasheets, technical guides, brochures and certificates by product and language.",
      },
      quality: {
        title: "Quality, Certificates and Standards | Metal Asia",
        description:
          "Review verified quality records, standards and certificates linked to approved source documents.",
      },
      services: {
        title: "Technical Support and Services | Metal Asia",
        description:
          "Request product selection support, technical documents and application-specific steel information.",
      },
      contact: {
        title: "Contact and Request a Quote | Metal Asia",
        description:
          "Send Metal Asia your product, thickness, quantity and technical requirements.",
      },
      privacy: {
        title: "Privacy Policy | Metal Asia",
        description: "Read the approved Metal Asia privacy and data-processing information.",
      },
      cookies: {
        title: "Cookie Policy | Metal Asia",
        description: "Read how Metal Asia uses essential and consent-based browser storage.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "Review verified properties, applications and available technical documents for {product}.",
      },
      industryDetail: {
        titleTemplate: "{industry} Steel Applications | Metal Asia",
        descriptionTemplate:
          "Explore documented specialist steel products and technical resources for {industry}.",
      },
    },
    accessibility: {
      skipToContent: "Skip to main content",
      mainNavigation: "Main navigation",
      languageSelector: "Language selector",
      selectLanguage: "Select language",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      productSearch: "Search the product catalogue",
      filters: "Product filters",
      removeFilter: "Remove filter {filter}",
      gridView: "Show products as cards",
      listView: "Show products as a list",
      compareSelection: "{count} products selected for comparison",
      documentPreview: "Technical document preview",
      previousImage: "Show previous image",
      nextImage: "Show next image",
      closeDialog: "Close dialog",
      externalLink: "External link",
      opensNewTab: "Opens in a new tab",
      requiredField: "Required field",
      errorSummary: "Form error summary",
      loading: "Content is loading",
    },
  },
  tk: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Önümleri görüň",
      requestQuote: "Teklip soraň",
      requestTechnicalInfo: "Tehniki maglumat soraň",
      learnMore: "Giňişleýin",
      viewAll: "Hemmesini görmek",
      download: "Ýüklemek",
      openInNewTab: "Täze sahypada açmak",
      search: "Gözlemek",
      clear: "Arassalamak",
      close: "Ýapmak",
      back: "Yza",
      next: "Indiki",
      previous: "Öňki",
      loading: "Ýüklenýär",
      unknown: "Näbelli",
      notAvailable: "Elýeterli däl",
      language: "Dil",
      menu: "Menýu",
    },
    nav: {
      home: "Baş sahypa",
      about: "Kompaniýa barada",
      products: "Önümler",
      industries: "Pudaklar",
      documents: "Tehniki resminamalar",
      quality: "Hil we standartlar",
      services: "Tehniki goldaw",
      contact: "Habarlaşmak",
      wear: "Aşynma deňeşdirişi",
    },
    home: {
      eyebrow: "Metal we polat listler, ýokary öndürijilikli polatlar",
      title: "Talaply ulanyşlar üçin tehniki polat çözgütleri",
      intro:
        "Ýokary berklikli, könelmä çydamly, sowut we gorag polat önümlerini barlanan tehniki maglumatlar hem-de anyk baglanan resminamalar arkaly öwreniň.",
      primaryCta: "Önümleri görüň",
      secondaryCta: "Teklip soraň",
      familiesTitle: "Önüm maşgalalary",
      familiesIntro:
        "Önümleri polat maşgalasy, ulanyş pudagy we bar bolan barlanan häsiýetleri boýunça tapyň.",
      featuredTitle: "Saýlanan önümler",
      featuredIntro:
        "Saýlanan markalary we çeşme resminamalaryna baglanan tehniki maglumatlary görüň.",
      industriesTitle: "Pudaklar we ulanyşlar",
      industriesIntro:
        "Polat görnüşlerini iş gurşawy we inženerçilik talaby boýunça saýlaň.",
      supportTitle: "Tehniki goldaw we hyzmatlar",
      supportText:
        "Önüm saýlamak, resminama almak we ulanyş sowallary boýunça kömek soraň.",
      qualityTitle: "Hil, şahadatnamalar we standartlar",
      qualityText:
        "Diňe tassyklanan çeşme resminamalary bilen subut edilen standartlar we şahadatnamalar görkezilýär.",
      documentsTitle: "Tehniki resminamalar merkezi",
      documentsText:
        "Bar bolan tehniki maglumat sahypalaryny, gollanmalary, broşýuralary we şahadatnamalary önüm hem-de dil boýunça gözläň.",
      aboutTitle: "Metal Asia barada",
      aboutText:
        "METAL ASIA demir we polat önümlerini üpjün ediji we BISALLOY®-yň resmi distribýutorydyr.",
      contactTitle: "Talabyňyzy ara alyp maslahatlaşalyň",
      contactText:
        "Esasly jogap üçin önüm, galyňlyk, mukdar we ulanyş boýunça zerur maglumatlary iberiň.",
    },
    about: {
      title: "Metal Asia barada",
      intro:
        "METAL ASIA demir we polat önümleriniň satuwy hem-de üpjünçiligi bilen meşgullanýar. Kompaniýa BISALLOY®-yň resmi distribýutorydyr.",
      positioningTitle: "Tehniki aýdyňlyga esaslanýar",
      positioningText:
        "Önüm baradaky maglumatlar çeşme resminamalaryna baglanýar, näbelli bahalar bolsa çaklanman, açyk görnüşde berilmedik diýlip bellenýär.",
      principlesTitle: "Maglumat ýörelgelerimiz",
      accuracyTitle: "Takyklyk",
      accuracyText:
        "Tehniki bahalar diňe tassyklanan çeşmä çenli yzarlanyp bilinýän mahaly çap edilýär.",
      traceabilityTitle: "Yzarlap bolujylyk",
      traceabilityText:
        "Önüm ýazgylarynda tassyklaýjy resminama we soňky barlag ýagdaýy görkezilýär.",
      supportTitle: "Maksatly goldaw",
      supportText:
        "Soraglar tehniki ýa-da söwda ýüzlenmesini dogry ugrukdyrmak üçin zerur ulanyş maglumatlaryny ýygnaýar.",
      roleDisclosure:
        "METAL ASIA demir we polat önümlerini satýar we üpjün edýär, BISALLOY® önümlerini bolsa resmi distribýutor hökmünde hödürleýär.",
    },
    products: {
      title: "Ähli önümler",
      intro:
        "Ýok tehniki bahalary nol hasaplaman ýörite polat markalaryny gözläň, süzüň we deňeşdiriň.",
      searchLabel: "Önümleri gözlemek",
      searchPlaceholder: "Önüm, maşgala ýa-da ulanyş boýunça gözlemek",
      resultCount: "{count} önüm",
      familyFilter: "Önüm maşgalasy",
      applicationFilter: "Ulanyş pudagy",
      hardnessFilter: "Brinell boýunça gatylyk",
      thicknessFilter: "Galyňlyk",
      yieldStrengthFilter: "Akma berkligi",
      tensileStrengthFilter: "Çekilme berkligi",
      viewLabel: "Netijeleriň görnüşi",
      gridView: "Kart görnüşi",
      listView: "Sanaw görnüşi",
      activeFilters: "Işjeň süzgüçler",
      clearFilters: "Ähli süzgüçleri arassalamak",
      compare: "Deňeşdirmek",
      addToCompare: "Deňeşdirmä goşmak",
      removeFromCompare: "Deňeşdirmeden aýyrmak",
      compareTitle: "Önümleri deňeşdirmek",
      compareHint: "Iki bilen dört aralygynda önüm saýlaň.",
      compareMinimum: "Deňeşdirmek üçin azyndan iki önüm saýlaň.",
      compareMaximum: "Dörde çenli önümi deňeşdirip bilersiňiz.",
      compareNow: "Saýlanan önümleri deňeşdirmek",
      emptyTitle: "Gabat gelýän önüm tapylmady",
      emptyText: "Gözlegi üýtgediň ýa-da bir ýa-da birnäçe süzgüji arassalaň.",
      missingNumericData:
        "Barlanan bahasy ýok önümler bu san süzgüjine goşulmaýar; ýetmeýän maglumat nol hasaplanmaýar.",
      viewDetails: "Giňişleýin görmek",
      requestDocument: "Tehniki resminama soramak",
      downloadDatasheet: "Tehniki maglumat sahypasyny ýüklemek",
    },
    product: {
      overview: "Umumy maglumat",
      gallery: "Önüm suratlary",
      advantages: "Esasy artykmaçlyklar",
      applications: "Ulanyş pudaklary",
      technicalSpecifications: "Tehniki häsiýetnamalar",
      thicknessRange: "Galyňlyk aralygy",
      brinellHardness: "Brinell boýunça gatylyk",
      yieldStrength: "Akma berkligi",
      proofStrength: "Şertli akma berkligi",
      tensileStrength: "Çekilme berkligi",
      chemicalComposition: "Himiki düzümi",
      mechanicalProperties: "Mehaniki häsiýetler",
      equivalentStandards: "Deň derejeli standartlar",
      certifications: "Barlanan şahadatnamalar",
      processingNotes: "Kesmek, kebşirlemek we egmek boýunça bellikler",
      cutting: "Kesmek",
      welding: "Kebşirlemek",
      bending: "Egmek",
      relatedProducts: "Baglanyşykly önümler",
      documentModule: "Tehniki maglumat sahypalary we goşmaça resminamalar",
      dataSource: "Çeşme resminamasy",
      lastVerified: "Soňky barlag",
      missingValue: "Barlanan baha elýeterli däl",
      missingDataNotice:
        "Diňe resmi resminamalar bilen tassyklanan meýdanlar görkezilýär. Berilmedik bahalar çaklanmady.",
      noDocumentTitle: "Tehniki resminama elýeterli däl",
      noDocumentText:
        "Bu önüme häzirlikçe tassyklanan resminama baglanmady. Ony tehniki topardan sorap bilersiňiz.",
      requestDocument: "Bu resminamany soramak",
      documentAvailableIn: "Bu resminama {language} dilinde elýeterlidir.",
      selectedLanguageUnavailable:
        "Saýlanan dilde tehniki resminama ýok. Bar bolan resminamalar öz dili görkezilip berilýär.",
    },
    industries: {
      title: "Pudaklar",
      intro:
        "Ýörite polat maglumatlaryny ulanyş gurşawy boýunça öwreniň. Önümiň laýyklygy barlanan tehniki talaplar boýunça tassyklanmalydyr.",
      exploreSector: "Bu pudagy öwrenmek",
      sectors: {
        defense: {
          title: "Goranmak we howpsuzlyk",
          description:
            "Ballistik we gorag inženerçilik bahalandyrmasy üçin resminamalaşdyrylan sowut hem-de gorag polat markalaryny görüň.",
        },
        mining: {
          title: "Magdançylyk we ýer gazuw işleri",
          description:
            "Güýçli könelýän gazyp almak we ýer işleri üçin könelmä çydamly hem-de ýokary berklikli markalary öwreniň.",
        },
        mineralProcessing: {
          title: "Minerallary gaýtadan işlemek",
          description:
            "Abraziw material akymyna sezewar bolýan ternawlar, örtükler, bunkerler we beýleki bölekler üçin resminamalaşdyrylan görnüşleri tapyň.",
        },
        construction: {
          title: "Gurluşyk",
          description:
            "Talaply gurluşlar we meýdan enjamlary üçin ýokary berklikli hem-de könelmä çydamly markalary deňeşdiriň.",
        },
        transportation: {
          title: "Ulag",
          description:
            "Agrama duýgur, ýük göterýän we könelmä sezewar ulag ulanyşlary üçin polat markalaryna baha beriň.",
        },
        energy: {
          title: "Energetika",
          description:
            "Talaply hyzmat şertindäki enjamlar we infrastruktura üçin barlanan polat maglumatlaryny öwreniň.",
        },
        agriculture: {
          title: "Oba hojalygy",
          description:
            "Toprak bilen işleýän we material geçirýän oba hojalyk bölekleri üçin könelmä çydamly markalary öwreniň.",
        },
        engineering: {
          title: "Umumy maşyn gurluşygy we taýýarlamak",
          description:
            "Inženerçilik bölekleri, maşynlar we taýýarlamagy meýilleşdirmek üçin resminamalaşdyrylan häsiýetleri deňeşdiriň.",
        },
      },
    },
    documents: {
      title: "Tehniki resminamalar merkezi",
      intro:
        "Tassyklanan tehniki maglumat sahypalaryny, gollanmalary, broşýuralary we şahadatnamalary önüm, maşgala, görnüş, dil we ýyl boýunça gözläň.",
      searchLabel: "Resminamalary gözlemek",
      searchPlaceholder: "Resminama ýa-da önüm ady boýunça gözlemek",
      familyFilter: "Önüm maşgalasy",
      productFilter: "Önüm",
      typeFilter: "Resminamanyň görnüşi",
      languageFilter: "Resminamanyň dili",
      yearFilter: "Çap edilen ýyly",
      datasheet: "Tehniki maglumat sahypasy",
      technicalGuide: "Tehniki gollanma",
      brochure: "Broşýura",
      certificate: "Şahadatnama",
      documentLanguage: "Resminamanyň dili",
      version: "Wersiýa",
      publicationDate: "Çap edilen senesi",
      fileSize: "Faýlyň göwrümi",
      lastUpdated: "Soňky täzelenme",
      source: "Çeşme",
      familyDatasheet: "Önüm maşgalasynyň tehniki maglumat sahypasy",
      selectedLanguageMissing:
        "Saýlanan interfeýs dilinde resminama ýok; bar bolan resminamalar öz hakyky dili bilen görkezilýär.",
      preview: "Resminamany öňünden görmek",
      previewUnavailable:
        "Bu enjamda öňünden görmek goldanylmaýar. Resminamany täze sahypada açyň ýa-da ýükläň.",
      emptyTitle: "Gabat gelýän resminama tapylmady",
      emptyText: "Gözlegi üýtgediň ýa-da bir ýa-da birnäçe resminama süzgüjini arassalaň.",
    },
    quality: {
      title: "Hil, şahadatnamalar we standartlar",
      intro:
        "Diňe tassyklanan resminamalar bilen subut edilen hil ýazgylaryny, standartlary we şahadatnamalary görüň.",
      verifiedOnlyTitle: "Diňe barlanan maglumat",
      verifiedOnlyText:
        "Standart ýa-da şahadatnama onuň çeşmesi we önüm bilen baglanyşygy tassyklanandan soň görkezilýär.",
      certificatesTitle: "Şahadatnamalar",
      noCertificatesTitle: "Tassyklanan şahadatnama çap edilmedi",
      noCertificatesText:
        "Degişli faýllar we olaryň ulanyş çägi barlanandan soň şahadatnamalar şu ýerde görkeziler.",
      standardsTitle: "Standartlar",
      traceabilityTitle: "Resminamalaryň yzarlap bolujylygy",
      traceabilityText:
        "Çap edilen ýazgylarda belli bolsa, çeşme, dil, wersiýa, çap senesi we barlag ýagdaýy saklanýar.",
    },
    services: {
      title: "Tehniki goldaw we hyzmatlar",
      intro:
        "Önüm saýlamak, tehniki resminama ýa-da ulanyşa degişli maglumat üçin düzülen sorag iberiň.",
      selectionTitle: "Önüm saýlamak boýunça goldaw",
      selectionText:
        "Resminamalaşdyrylan häsiýetleri deňeşdiriň we goşmaça baha bermek üçin ulanyş talaplaryňyzy iberiň.",
      documentsTitle: "Resminama goldawy",
      documentsText:
        "Elýeterli däl tehniki maglumat sahypasyny soraň ýa-da bar resminamanyň dilini we wersiýasyny anyklaň.",
      processingTitle: "Işlemek boýunça maglumat",
      processingText:
        "Kesmek, kebşirlemek we egmek boýunça görkezmeler diňe resmi önüm resminamalary bilen tassyklananda berilýär.",
      enquiryTitle: "Tehniki sorag",
      enquiryText:
        "Soraga takyk baha bermek üçin marka, galyňlyk, mukdar we iş şertlerini görkeziň.",
    },
    contact: {
      title: "Habarlaşmak we teklip soramak",
      intro:
        "Baha berýän önümiňizi we tehniki talabyňyzy habar beriň.",
      detailsTitle: "Aragatnaşyk maglumatlary",
      detailsUnavailable:
        "Barlanan elektron poçta, telefon, WhatsApp we salgy maglumatlary häzirlikçe çap etmek üçin sazlanmady.",
      quoteTitle: "Teklip soraň",
      quoteText:
        "Esasly jogap üçin önüm, galyňlyk, mukdar we ulanyş maglumatlaryny beriň.",
    },
    form: {
      fullName: "Ady we familiýasy",
      company: "Kompaniýa",
      email: "Elektron poçta",
      phone: "Telefon",
      country: "Ýurt",
      city: "Şäher",
      address: "Salgy",
      product: "Gyzyklandyrýan önüm",
      thickness: "Gerekli galyňlyk",
      quantity: "Mukdar",
      message: "Habar",
      privacyConsent: "Ýüz tutmamyň işlenişi baradaky gizlinlik bildirişini okadym.",
      privacyLinkText: "Gizlinlik syýasatyny okamak",
      selectPlaceholder: "Görnüşi saýlaň",
      optional: "Hökmany däl",
      submit: "Soragy ibermek",
      submitting: "Sorag iberilýär",
      required: "Bu meýdan hökmany.",
      invalidEmail: "Dogry elektron poçta salgysyny giriziň.",
      invalidPhone: "Dogry telefon belgisini giriziň.",
      errorSummaryTitle: "Aşakdaky meýdanlary düzediň",
      spamDetected: "Soragy barlamak başartmady. Gaýtadan synanyşyň.",
      successTitle: "Soragyňyz kabul edildi",
      successText:
        "Sazlanan hyzmat soragyňyzyň alnandygyny tassyklady. Soň habarlaşmak üçin görkezilen belgini saklaň.",
      errorTitle: "Soragyňyz iberilmedi",
      errorText:
        "Hyzmat soragy kabul edip bilmedi. Meýdanlary barlap, soňrak gaýtadan synanyşyň.",
      integrationMissingTitle: "E-poçta arkaly ibermek ulanylar",
      integrationMissingText:
        "Serwer arkaly ibermek entek birikdirilmedi. Barlagdan soň sorag e-poçta programmaňyzda taýýarlanan habar hökmünde açylar we diňe şol ýerde tassyklaňyzdan soň ugradylar.",
    },
    legal: {
      privacyTitle: "Gizlinlik syýasaty",
      privacyBlockerTitle: "Tassyklanan gizlinlik teksti gerek",
      privacyBlockerText:
        "Hukuk şahsy, maglumat jogapkärçiligi, işlemegiň maksatlary, saklaýyş möhletleri we aragatnaşyk maglumatlary tassyklanýança gizlinlik syýasaty çap edilmez.",
      cookiesTitle: "Cookie faýllarynyň syýasaty",
      cookiesBlockerTitle: "Cookie faýllary boýunça tassyklanan tekst gerek",
      cookiesBlockerText:
        "Ulanylýan faýllar, maksatlary, möhletleri, üpjün edijileri we razylyk tertibi tassyklanýança syýasat çap edilmez.",
      preferenceStorageText:
        "Hökmany dil saýlawynyň saklanyşy seljeriş we marketing razylygyndan aýratyn alnyp barylýar.",
      legalReviewRequired: "Çap etmezden öň hukuk barlagy zerurdyr.",
    },
    breadcrumbs: {
      label: "Sahypa ýoly",
      home: "Baş sahypa",
      currentPage: "Häzirki sahypa",
    },
    notFound: {
      title: "Sahypa tapylmady",
      text:
        "Salgy üýtgän bolup biler ýa-da bu sahypa saýlanan dilde elýeterli däldir.",
      backHome: "Baş sahypa dolanmak",
      browseProducts: "Ähli önümleri görmek",
    },
    footer: {
      tagline:
        "Ýörite polada baha bermek üçin barlanan önüm maglumatlary we tehniki resminamalar.",
      productLinksTitle: "Önümler",
      companyLinksTitle: "Kompaniýa",
      resourceLinksTitle: "Çeşmeler",
      sitemapLabel: "XML saýt kartasy",
      contactTitle: "Habarlaşmak",
      rights: "Ähli hukuklar goralan.",
      legalEntityMissing: "Barlanan hukuk şahsy maglumatlary çap edilmäge garaşýar.",
      contactDetailsMissing: "Barlanan aragatnaşyk maglumatlary çap edilmäge garaşýar.",
    },
    seo: {
      home: {
        title: "Polat list we metal list | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA BISALLOY® polat listlerini, metal listleri, könelmä çydamly listleri, ýokary berklikli gurluş we sowut polatlaryny tehniki resminamalar bilen üpjün edýär.",
      },
      about: {
        title: "Metal Asia barada",
        description:
          "METAL ASIA demir we polat önümlerini satyjy we üpjün ediji, şeýle hem BISALLOY®-yň resmi distribýutorydyr.",
      },
      products: {
        title: "Polat list we könelmä çydamly polat | METAL ASIA",
        description:
          "Ýokary berklikli, könelmä çydamly, sowut we gorag polat markalaryny gözläň, süzüň we deňeşdiriň.",
      },
      industries: {
        title: "Pudaklar we ulanyşlar | Metal Asia",
        description:
          "Resminamalaşdyrylan ýörite polat görnüşlerini pudak, iş gurşawy we inženerçilik talaby boýunça öwreniň.",
      },
      documents: {
        title: "Tehniki resminamalar | Metal Asia",
        description:
          "Bar bolan polat maglumat sahypalaryny, tehniki gollanmalary, broşýuralary we şahadatnamalary önüm hem-de dil boýunça gözläň.",
      },
      quality: {
        title: "Hil, şahadatnamalar we standartlar | Metal Asia",
        description:
          "Tassyklanan çeşme resminamalaryna baglanan barlanan hil ýazgylaryny, standartlary we şahadatnamalary görüň.",
      },
      services: {
        title: "Tehniki goldaw we hyzmatlar | Metal Asia",
        description:
          "Önüm saýlamak boýunça goldaw, tehniki resminamalar we ulanyşa degişli polat maglumatyny soraň.",
      },
      contact: {
        title: "Habarlaşmak we teklip soramak | Metal Asia",
        description:
          "Metal Asia kompaniýasyna önüm, galyňlyk, mukdar we tehniki talaplaryňyzy iberiň.",
      },
      privacy: {
        title: "Gizlinlik syýasaty | Metal Asia",
        description:
          "Metal Asia kompaniýasynyň tassyklanan gizlinlik we maglumat işlemek şertlerini okaň.",
      },
      cookies: {
        title: "Cookie faýllarynyň syýasaty | Metal Asia",
        description:
          "Metal Asia hökmany we razylyga esaslanýan brauzer saklanyşyny nähili ulanýandygyny okaň.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "{product} üçin barlanan häsiýetleri, ulanyşlary we bar bolan tehniki resminamalary görüň.",
      },
      industryDetail: {
        titleTemplate: "{industry} üçin polat ulanyşlary | Metal Asia",
        descriptionTemplate:
          "{industry} üçin resminamalaşdyrylan ýörite polat önümlerini we tehniki çeşmeleri öwreniň.",
      },
    },
    accessibility: {
      skipToContent: "Esasy mazmuna geçmek",
      mainNavigation: "Esasy nawigasiýa",
      languageSelector: "Dil saýlamak",
      selectLanguage: "Dili saýlaň",
      openMenu: "Menýuny açmak",
      closeMenu: "Menýuny ýapmak",
      productSearch: "Önümler katalogynda gözlemek",
      filters: "Önüm süzgüçleri",
      removeFilter: "{filter} süzgüjini aýyrmak",
      gridView: "Önümleri kart görnüşinde görkezmek",
      listView: "Önümleri sanaw görnüşinde görkezmek",
      compareSelection: "Deňeşdirmek üçin {count} önüm saýlandy",
      documentPreview: "Tehniki resminamany öňünden görmek",
      previousImage: "Öňki suraty görkezmek",
      nextImage: "Indiki suraty görkezmek",
      closeDialog: "Gepleşik penjiresini ýapmak",
      externalLink: "Daşarky baglanyşyk",
      opensNewTab: "Täze sahypada açylýar",
      requiredField: "Hökmany meýdan",
      errorSummary: "Formadaky ýalňyşlaryň jemlemesi",
      loading: "Mazmun ýüklenýär",
    },
  },
  tg: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Маҳсулотро бинед",
      requestQuote: "Пешниҳоди нарх дархост кунед",
      requestTechnicalInfo: "Маълумоти техникӣ дархост кунед",
      learnMore: "Муфассал",
      viewAll: "Ҳамаро дидан",
      download: "Боргирӣ",
      openInNewTab: "Дар варақаи нав кушодан",
      search: "Ҷустуҷӯ",
      clear: "Тоза кардан",
      close: "Пӯшидан",
      back: "Бозгашт",
      next: "Баъдӣ",
      previous: "Қаблӣ",
      loading: "Бор шуда истодааст",
      unknown: "Номаълум",
      notAvailable: "Дастрас нест",
      language: "Забон",
      menu: "Меню",
    },
    nav: {
      home: "Саҳифаи асосӣ",
      about: "Дар бораи ширкат",
      products: "Маҳсулот",
      industries: "Соҳаҳо",
      documents: "Ҳуҷҷатҳои техникӣ",
      quality: "Сифат ва стандартҳо",
      services: "Дастгирии техникӣ",
      contact: "Тамос",
      wear: "Муқоисаи фарсудашавӣ",
    },
    home: {
      eyebrow: "Лавҳаи металлӣ, варақи пӯлод ва пӯлодҳои самаранокиашон баланд",
      title: "Роҳҳалҳои техникии пӯлод барои шароити мураккаб",
      intro:
        "Маҳсулоти пӯлоди мустаҳкамаш баланд, ба фарсудашавӣ тобовар, зиреҳӣ ва муҳофизатиро тавассути маълумоти санҷидашуда ва ҳуҷҷатҳои дақиқ пайвастшуда омӯзед.",
      primaryCta: "Маҳсулотро бинед",
      secondaryCta: "Пешниҳоди нарх дархост кунед",
      familiesTitle: "Оилаҳои маҳсулот",
      familiesIntro:
        "Маҳсулотро аз рӯи оилаи пӯлод, соҳаи истифода ва хосиятҳои санҷидашудаи дастрас пайдо кунед.",
      featuredTitle: "Маҳсулоти интихобшуда",
      featuredIntro:
        "Навъҳои интихобшуда ва маълумоти техникии ба ҳуҷҷатҳои манбаъ пайвастшударо бинед.",
      industriesTitle: "Соҳаҳо ва истифодаҳо",
      industriesIntro:
        "Гунаҳои пӯлодро аз рӯи муҳити корӣ ва талаботи муҳандисӣ интихоб кунед.",
      supportTitle: "Дастгирии техникӣ ва хизматрасонӣ",
      supportText:
        "Барои интихоби маҳсулот, дастрасӣ ба ҳуҷҷат ва саволҳои истифода кумак дархост кунед.",
      qualityTitle: "Сифат, шаҳодатномаҳо ва стандартҳо",
      qualityText:
        "Танҳо стандартҳо ва шаҳодатномаҳои бо ҳуҷҷатҳои тасдиқшуда собитшуда нишон дода мешаванд.",
      documentsTitle: "Маркази ҳуҷҷатҳои техникӣ",
      documentsText:
        "Варақаҳои маълумоти техникӣ, дастурҳо, китобчаҳо ва шаҳодатномаҳои дастрасро аз рӯи маҳсулот ва забон ҷустуҷӯ кунед.",
      aboutTitle: "Дар бораи Metal Asia",
      aboutText:
        "METAL ASIA таъминкунандаи маҳсулоти оҳану пӯлод ва дистрибютори расмии BISALLOY® мебошад.",
      contactTitle: "Талаботи худро баррасӣ кунед",
      contactText:
        "Барои ҷавоби асоснок маълумоти зарурии маҳсулот, ғафсӣ, миқдор ва соҳаи истифодаро фиристед.",
    },
    about: {
      title: "Дар бораи Metal Asia",
      intro:
        "METAL ASIA ба фурӯш ва таъминоти маҳсулоти оҳану пӯлод машғул аст. Ширкат дистрибютори расмии BISALLOY® мебошад.",
      positioningTitle: "Бар пояи возеҳии техникӣ",
      positioningText:
        "Иддаоҳои маҳсулот ба ҳуҷҷатҳои манбаъ пайваст мешаванд, арзишҳои номаълум бошанд тахмин нашуда, ошкоро ҳамчун маълумоти пешниҳоднашуда мемонанд.",
      principlesTitle: "Принсипҳои кор бо маълумот",
      accuracyTitle: "Дақиқӣ",
      accuracyText:
        "Арзишҳои техникӣ танҳо ҳангоми пайгирӣ то манбаи тасдиқшуда нашр мешаванд.",
      traceabilityTitle: "Пайгиришавандагӣ",
      traceabilityText:
        "Сабти маҳсулот ҳуҷҷати тасдиқкунанда ва ҳолати санҷиши охиринро нишон медиҳад.",
      supportTitle: "Дастгирии мақсаднок",
      supportText:
        "Дархостҳо маълумоти истифодаеро ҷамъ мекунанд, ки барои равона кардани муроҷиати техникӣ ё тиҷоратӣ зарур аст.",
      roleDisclosure:
        "METAL ASIA маҳсулоти оҳану пӯлодро мефурӯшад ва таъмин мекунад, инчунин маҳсулоти BISALLOY®-ро ҳамчун дистрибютори расмӣ пешниҳод менамояд.",
    },
    products: {
      title: "Ҳамаи маҳсулот",
      intro:
        "Навъҳои махсуси пӯлодро бе сифр ҳисоб кардани арзишҳои техникии дастнорас ҷустуҷӯ, полоиш ва муқоиса кунед.",
      searchLabel: "Ҷустуҷӯи маҳсулот",
      searchPlaceholder: "Аз рӯи маҳсулот, оила ё соҳаи истифода ҷустуҷӯ кунед",
      resultCount: "{count} маҳсулот",
      familyFilter: "Оилаи маҳсулот",
      applicationFilter: "Соҳаи истифода",
      hardnessFilter: "Сахтӣ аз рӯи Бринелл",
      thicknessFilter: "Ғафсӣ",
      yieldStrengthFilter: "Ҳадди ҷорӣшавӣ",
      tensileStrengthFilter: "Мустаҳкамӣ ҳангоми кашиш",
      viewLabel: "Намуди натиҷаҳо",
      gridView: "Намуди кортӣ",
      listView: "Намуди рӯйхат",
      activeFilters: "Полоишҳои фаъол",
      clearFilters: "Ҳамаи полоишҳоро тоза кардан",
      compare: "Муқоиса",
      addToCompare: "Ба муқоиса илова кардан",
      removeFromCompare: "Аз муқоиса гирифтан",
      compareTitle: "Муқоисаи маҳсулот",
      compareHint: "Аз ду то чор маҳсулот интихоб кунед.",
      compareMinimum: "Барои муқоиса камаш ду маҳсулот интихоб кунед.",
      compareMaximum: "То чор маҳсулотро муқоиса кардан мумкин аст.",
      compareNow: "Маҳсулоти интихобшударо муқоиса кардан",
      emptyTitle: "Маҳсулоти мувофиқ ёфт нашуд",
      emptyText: "Ҷустуҷӯро тағйир диҳед ё як ё чанд полоишро тоза кунед.",
      missingNumericData:
        "Маҳсулоти бе арзиши санҷидашуда ба ин полоиши рақамӣ дохил намешавад; маълумоти нокифоя сифр ҳисобида намешавад.",
      viewDetails: "Муфассал дидан",
      requestDocument: "Ҳуҷҷати техникӣ дархост кардан",
      downloadDatasheet: "Варақаи маълумоти техникиро боргирӣ кардан",
    },
    product: {
      overview: "Шарҳи умумӣ",
      gallery: "Тасвирҳои маҳсулот",
      advantages: "Бартариҳои асосӣ",
      applications: "Соҳаҳои истифода",
      technicalSpecifications: "Хусусиятҳои техникӣ",
      thicknessRange: "Доираи ғафсӣ",
      brinellHardness: "Сахтӣ аз рӯи Бринелл",
      yieldStrength: "Ҳадди ҷорӣшавӣ",
      proofStrength: "Ҳадди шартии ҷорӣшавӣ",
      tensileStrength: "Мустаҳкамӣ ҳангоми кашиш",
      chemicalComposition: "Таркиби кимиёвӣ",
      mechanicalProperties: "Хосиятҳои механикӣ",
      equivalentStandards: "Стандартҳои ҳамарзиш",
      certifications: "Шаҳодатномаҳои санҷидашуда",
      processingNotes: "Тавзеҳоти буридан, кафшеркунӣ ва хамкунӣ",
      cutting: "Буридан",
      welding: "Кафшеркунӣ",
      bending: "Хамкунӣ",
      relatedProducts: "Маҳсулоти вобаста",
      documentModule: "Варақаҳои маълумоти техникӣ ва ҳуҷҷатҳои иловагӣ",
      dataSource: "Ҳуҷҷати манбаъ",
      lastVerified: "Санҷиши охирин",
      missingValue: "Арзиши санҷидашуда дастрас нест",
      missingDataNotice:
        "Танҳо майдонҳои бо ҳуҷҷатҳои расмӣ тасдиқшуда нишон дода мешаванд. Арзишҳои пешниҳоднашуда тахмин нашудаанд.",
      noDocumentTitle: "Ҳуҷҷати техникӣ дастрас нест",
      noDocumentText:
        "Ба ин маҳсулот ҳоло ҳуҷҷати тасдиқшуда пайваст нест. Шумо метавонед онро аз гурӯҳи техникӣ дархост кунед.",
      requestDocument: "Ин ҳуҷҷатро дархост кардан",
      documentAvailableIn: "Ин ҳуҷҷат ба забони {language} дастрас аст.",
      selectedLanguageUnavailable:
        "Ҳуҷҷати техникӣ ба забони интихобшуда нест. Ҳуҷҷатҳои дастрас бо нишон додани забони худ оварда мешаванд.",
    },
    industries: {
      title: "Соҳаҳо",
      intro:
        "Маълумоти пӯлоди махсусро аз рӯи муҳити истифода омӯзед. Мувофиқати маҳсулот бояд бо талаботи техникии санҷидашуда тасдиқ шавад.",
      exploreSector: "Ин соҳаро омӯзед",
      sectors: {
        defense: {
          title: "Мудофиа ва амният",
          description:
            "Навъҳои ҳуҷҷатшудаи пӯлоди зиреҳӣ ва муҳофизатиро барои арзёбии баллистикӣ ва муҳандисии муҳофизатӣ бинед.",
        },
        mining: {
          title: "Истихроҷи маъдан ва заминканӣ",
          description:
            "Навъҳои ба фарсудашавӣ тобовар ва мустаҳкамаш баландро барои корҳои истихроҷ ва заминкании дорои фарсудашавии шадид омӯзед.",
        },
        mineralProcessing: {
          title: "Коркарди маъдан",
          description:
            "Барои новаҳо, рӯйпӯшҳо, бункерҳо ва дигар қисмҳои зери ҷараёни маводи абразивӣ гунаҳои ҳуҷҷатшударо пайдо кунед.",
        },
        construction: {
          title: "Сохтмон",
          description:
            "Навъҳои мустаҳкамаш баланд ва ба фарсудашавӣ тобоварро барои сохтор ва таҷҳизоти мураккаби сохтмонӣ муқоиса кунед.",
        },
        transportation: {
          title: "Нақлиёт",
          description:
            "Навъҳои пӯлодро барои истифодаҳои нақлиётии ҳассос ба вазн, борбардор ва зери фарсудашавӣ арзёбӣ кунед.",
        },
        energy: {
          title: "Энергетика",
          description:
            "Маълумоти санҷидашудаи пӯлодро барои таҷҳизот ва зерсохтор дар шароити мураккаби хизмат омӯзед.",
        },
        agriculture: {
          title: "Кишоварзӣ",
          description:
            "Навъҳои ба фарсудашавӣ тобоварро барои қисмҳои кишоварзии бо хок коркунанда ва маводкаш омӯзед.",
        },
        engineering: {
          title: "Мошинсозӣ ва омодасозии умумӣ",
          description:
            "Хосиятҳои ҳуҷҷатшударо барои қисмҳои муҳандисӣ, мошинҳо ва банақшагирии омодасозӣ муқоиса кунед.",
        },
      },
    },
    documents: {
      title: "Маркази ҳуҷҷатҳои техникӣ",
      intro:
        "Варақаҳои маълумоти техникӣ, дастурҳо, китобчаҳо ва шаҳодатномаҳои тасдиқшударо аз рӯи маҳсулот, оила, навъ, забон ва сол ҷустуҷӯ кунед.",
      searchLabel: "Ҷустуҷӯи ҳуҷҷатҳо",
      searchPlaceholder: "Аз рӯи номи ҳуҷҷат ё маҳсулот ҷустуҷӯ кунед",
      familyFilter: "Оилаи маҳсулот",
      productFilter: "Маҳсулот",
      typeFilter: "Навъи ҳуҷҷат",
      languageFilter: "Забони ҳуҷҷат",
      yearFilter: "Соли нашр",
      datasheet: "Варақаи маълумоти техникӣ",
      technicalGuide: "Дастури техникӣ",
      brochure: "Китобча",
      certificate: "Шаҳодатнома",
      documentLanguage: "Забони ҳуҷҷат",
      version: "Нусха",
      publicationDate: "Санаи нашр",
      fileSize: "Андозаи файл",
      lastUpdated: "Навсозии охирин",
      source: "Манбаъ",
      familyDatasheet: "Варақаи маълумоти оилаи маҳсулот",
      selectedLanguageMissing:
        "Ба забони интихобшудаи сомона ҳуҷҷат нест; ҳуҷҷатҳои дастрас бо забони воқеии худ нишон дода мешаванд.",
      preview: "Пешнамоиши ҳуҷҷат",
      previewUnavailable:
        "Пешнамоиш дар ин дастгоҳ дастгирӣ намешавад. Ҳуҷҷатро дар варақаи нав кушоед ё боргирӣ кунед.",
      emptyTitle: "Ҳуҷҷати мувофиқ ёфт нашуд",
      emptyText: "Ҷустуҷӯро тағйир диҳед ё як ё чанд полоиши ҳуҷҷатро тоза кунед.",
    },
    quality: {
      title: "Сифат, шаҳодатномаҳо ва стандартҳо",
      intro:
        "Танҳо сабтҳои сифат, стандартҳо ва шаҳодатномаҳои бо ҳуҷҷатҳои тасдиқшуда собитшударо бинед.",
      verifiedOnlyTitle: "Танҳо маълумоти санҷидашуда",
      verifiedOnlyText:
        "Стандарт ё шаҳодатнома танҳо пас аз тасдиқи манбаъ ва иртиботи он бо маҳсулот нишон дода мешавад.",
      certificatesTitle: "Шаҳодатномаҳо",
      noCertificatesTitle: "Шаҳодатномаи тасдиқшуда нашр нашудааст",
      noCertificatesText:
        "Шаҳодатномаҳо пас аз санҷиши файлҳои дахлдор ва доираи амали онҳо дар ин ҷо нишон дода мешаванд.",
      standardsTitle: "Стандартҳо",
      traceabilityTitle: "Пайгиришавандагии ҳуҷҷатҳо",
      traceabilityText:
        "Дар сабтҳои нашршуда манбаъ, забон, нусха, санаи нашр ва ҳолати санҷиш, агар маълум бошанд, нигоҳ дошта мешаванд.",
    },
    services: {
      title: "Дастгирии техникӣ ва хизматрасонӣ",
      intro:
        "Барои интихоби маҳсулот, ҳуҷҷати техникӣ ё маълумоти вобаста ба истифода дархости сохтордор фиристед.",
      selectionTitle: "Дастгирӣ дар интихоби маҳсулот",
      selectionText:
        "Хосиятҳои ҳуҷҷатшударо муқоиса карда, талаботи истифодаи худро барои арзёбии минбаъда фиристед.",
      documentsTitle: "Дастгирии ҳуҷҷатӣ",
      documentsText:
        "Варақаи маълумоти техникии дастнорасро дархост кунед ё забон ва нусхаи ҳуҷҷати мавҷударо дақиқ намоед.",
      processingTitle: "Маълумоти коркард",
      processingText:
        "Роҳнамоии буридан, кафшеркунӣ ва хамкунӣ танҳо ҳангоми тасдиқ бо ҳуҷҷатҳои расмии маҳсулот дода мешавад.",
      enquiryTitle: "Дархости техникӣ",
      enquiryText:
        "Барои арзёбии дақиқи дархост навъ, ғафсӣ, миқдор ва шароити корро нишон диҳед.",
    },
    contact: {
      title: "Тамос ва дархости пешниҳод",
      intro:
        "Маҳсулот ва талаботи техникиеро, ки арзёбӣ мекунед, хабар диҳед.",
      detailsTitle: "Маълумоти тамос",
      detailsUnavailable:
        "Почтаи электронӣ, телефон, WhatsApp ва нишонии санҷидашуда ҳоло барои нашр танзим нашудаанд.",
      quoteTitle: "Пешниҳоди нарх дархост кунед",
      quoteText:
        "Барои ҷавоби асоснок маҳсулот, ғафсӣ, миқдор ва маълумоти истифодаро пешниҳод кунед.",
    },
    form: {
      fullName: "Ному насаб",
      company: "Ширкат",
      email: "Почтаи электронӣ",
      phone: "Телефон",
      country: "Кишвар",
      city: "Шаҳр",
      address: "Суроға",
      product: "Маҳсулоти мавриди таваҷҷуҳ",
      thickness: "Ғафсии лозим",
      quantity: "Миқдор",
      message: "Паём",
      privacyConsent: "Огоҳиномаи махфиятро дар бораи коркарди дархостам хондам.",
      privacyLinkText: "Сиёсати махфиятро хонед",
      selectPlaceholder: "Гунаро интихоб кунед",
      optional: "Ихтиёрӣ",
      submit: "Дархостро фиристодан",
      submitting: "Дархост фиристода мешавад",
      required: "Ин майдон ҳатмист.",
      invalidEmail: "Нишонии дурусти почтаи электрониро ворид кунед.",
      invalidPhone: "Рақами дурусти телефонро ворид кунед.",
      errorSummaryTitle: "Майдонҳои зеринро ислоҳ кунед",
      spamDetected: "Санҷидани дархост имкон надошт. Аз нав кӯшиш кунед.",
      successTitle: "Дархости шумо қабул шуд",
      successText:
        "Хизмати танзимшуда қабули дархостро тасдиқ кард. Рақами нишоншударо барои пайгирӣ нигоҳ доред.",
      errorTitle: "Дархости шумо фиристода нашуд",
      errorText:
        "Хизмат дархостро қабул карда натавонист. Майдонҳоро санҷида, баъдтар аз нав кӯшиш кунед.",
      integrationMissingTitle: "Ирсол тавассути почта истифода мешавад",
      integrationMissingText:
        "Ирсол тавассути сервер ҳоло пайваст нест. Пас аз санҷиш дархост дар барномаи почтаи шумо ҳамчун паёми омода кушода мешавад ва танҳо баъди тасдиқи шумо фиристода мешавад.",
    },
    legal: {
      privacyTitle: "Сиёсати махфият",
      privacyBlockerTitle: "Матни тасдиқшудаи махфият лозим аст",
      privacyBlockerText:
        "То тасдиқи шахси ҳуқуқӣ, масъули маълумот, ҳадафҳои коркард, муҳлатҳои нигоҳдорӣ ва маълумоти тамос сиёсати махфият нашр намешавад.",
      cookiesTitle: "Сиёсати файлҳои cookie",
      cookiesBlockerTitle: "Матни тасдиқшудаи файлҳои cookie лозим аст",
      cookiesBlockerText:
        "То тасдиқи файлҳои истифодашаванда, ҳадафҳо, муҳлатҳо, таъминкунандагон ва раванди ризоият сиёсат нашр намешавад.",
      preferenceStorageText:
        "Нигоҳдории ҳатмии интихоби забон аз ризоияти таҳлилӣ ва бозорёбӣ ҷудо аст.",
      legalReviewRequired: "Пеш аз нашр санҷиши ҳуқуқӣ лозим аст.",
    },
    breadcrumbs: {
      label: "Роҳи саҳифа",
      home: "Саҳифаи асосӣ",
      currentPage: "Саҳифаи ҷорӣ",
    },
    notFound: {
      title: "Саҳифа ёфт нашуд",
      text:
        "Нишонӣ шояд тағйир ёфтааст ё ин саҳифа ба забони интихобшуда дастрас нест.",
      backHome: "Бозгашт ба саҳифаи асосӣ",
      browseProducts: "Ҳамаи маҳсулотро дидан",
    },
    footer: {
      tagline:
        "Маълумоти санҷидашудаи маҳсулот ва ҳуҷҷатҳои техникӣ барои арзёбии пӯлоди махсус.",
      productLinksTitle: "Маҳсулот",
      companyLinksTitle: "Ширкат",
      resourceLinksTitle: "Манбаъҳо",
      sitemapLabel: "Харитаи XML-и сайт",
      contactTitle: "Тамос",
      rights: "Ҳамаи ҳуқуқҳо ҳифз шудаанд.",
      legalEntityMissing: "Маълумоти санҷидашудаи шахси ҳуқуқӣ интизори нашр аст.",
      contactDetailsMissing: "Маълумоти санҷидашудаи тамос интизори нашр аст.",
    },
    seo: {
      home: {
        title: "Варақи пӯлод ва лавҳаи металлӣ | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA лавҳаҳои пӯлоди BISALLOY®, варақи металлӣ, лавҳаи ба фарсудашавӣ тобовар, пӯлоди конструксионии мустаҳкам ва зиреҳиро бо ҳуҷҷатҳои техникӣ пешниҳод мекунад.",
      },
      about: {
        title: "Дар бораи Metal Asia",
        description:
          "METAL ASIA фурӯшанда ва таъминкунандаи маҳсулоти оҳану пӯлод, инчунин дистрибютори расмии BISALLOY® мебошад.",
      },
      products: {
        title: "Варақи пӯлод ва пӯлоди ба фарсудашавӣ тобовар | METAL ASIA",
        description:
          "Навъҳои пӯлоди мустаҳкамаш баланд, ба фарсудашавӣ тобовар, зиреҳӣ ва муҳофизатиро ҷустуҷӯ, полоиш ва муқоиса кунед.",
      },
      industries: {
        title: "Соҳаҳо ва истифодаҳо | Metal Asia",
        description:
          "Гунаҳои ҳуҷҷатшудаи пӯлоди махсусро аз рӯи соҳа, муҳити кор ва талаботи муҳандисӣ омӯзед.",
      },
      documents: {
        title: "Ҳуҷҷатҳои техникӣ | Metal Asia",
        description:
          "Варақаҳои маълумот, дастурҳои техникӣ, китобчаҳо ва шаҳодатномаҳои дастрасро аз рӯи маҳсулот ва забон ҷустуҷӯ кунед.",
      },
      quality: {
        title: "Сифат, шаҳодатномаҳо ва стандартҳо | Metal Asia",
        description:
          "Сабтҳои санҷидашудаи сифат, стандартҳо ва шаҳодатномаҳои ба ҳуҷҷатҳои тасдиқшуда пайвастшударо бинед.",
      },
      services: {
        title: "Дастгирии техникӣ ва хизматрасонӣ | Metal Asia",
        description:
          "Барои интихоби маҳсулот, ҳуҷҷатҳои техникӣ ва маълумоти пӯлод барои истифодаи мушаххас кумак дархост кунед.",
      },
      contact: {
        title: "Тамос ва дархости пешниҳод | Metal Asia",
        description:
          "Ба Metal Asia маълумоти маҳсулот, ғафсӣ, миқдор ва талаботи техникиро фиристед.",
      },
      privacy: {
        title: "Сиёсати махфият | Metal Asia",
        description:
          "Маълумоти тасдиқшудаи Metal Asia оид ба махфият ва коркарди маълумотро хонед.",
      },
      cookies: {
        title: "Сиёсати файлҳои cookie | Metal Asia",
        description:
          "Бихонед, ки Metal Asia нигоҳдории ҳатмӣ ва вобаста ба ризоиятро дар браузер чӣ гуна истифода мекунад.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "Хосиятҳои санҷидашуда, истифодаҳо ва ҳуҷҷатҳои техникии дастрасро барои {product} бинед.",
      },
      industryDetail: {
        titleTemplate: "Истифодаи пӯлод барои {industry} | Metal Asia",
        descriptionTemplate:
          "Маҳсулоти ҳуҷҷатшудаи пӯлоди махсус ва манбаъҳои техникиро барои {industry} омӯзед.",
      },
    },
    accessibility: {
      skipToContent: "Гузариш ба муҳтавои асосӣ",
      mainNavigation: "Роҳнамоии асосӣ",
      languageSelector: "Интихоби забон",
      selectLanguage: "Забонро интихоб кунед",
      openMenu: "Менюро кушодан",
      closeMenu: "Менюро пӯшидан",
      productSearch: "Ҷустуҷӯ дар феҳристи маҳсулот",
      filters: "Полоишҳои маҳсулот",
      removeFilter: "Полоиши {filter}-ро гирифтан",
      gridView: "Маҳсулотро ҳамчун корт нишон додан",
      listView: "Маҳсулотро ҳамчун рӯйхат нишон додан",
      compareSelection: "Барои муқоиса {count} маҳсулот интихоб шуд",
      documentPreview: "Пешнамоиши ҳуҷҷати техникӣ",
      previousImage: "Тасвири қаблиро нишон додан",
      nextImage: "Тасвири баъдиро нишон додан",
      closeDialog: "Равзанаи гуфтугӯро пӯшидан",
      externalLink: "Пайванди беруна",
      opensNewTab: "Дар варақаи нав кушода мешавад",
      requiredField: "Майдони ҳатмӣ",
      errorSummary: "Хулосаи хатоҳои шакл",
      loading: "Муҳтаво бор шуда истодааст",
    },
  },
  uz: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Mahsulotlarni ko‘rish",
      requestQuote: "Taklif so‘rash",
      requestTechnicalInfo: "Texnik ma’lumot so‘rash",
      learnMore: "Batafsil",
      viewAll: "Barchasini ko‘rish",
      download: "Yuklab olish",
      openInNewTab: "Yangi varaqda ochish",
      search: "Qidirish",
      clear: "Tozalash",
      close: "Yopish",
      back: "Orqaga",
      next: "Keyingi",
      previous: "Oldingi",
      loading: "Yuklanmoqda",
      unknown: "Noma’lum",
      notAvailable: "Mavjud emas",
      language: "Til",
      menu: "Menyu",
    },
    nav: {
      home: "Bosh sahifa",
      about: "Kompaniya haqida",
      products: "Mahsulotlar",
      industries: "Tarmoqlar",
      documents: "Texnik hujjatlar",
      quality: "Sifat va standartlar",
      services: "Texnik yordam",
      contact: "Aloqa",
      wear: "Ерошишни солиштириш",
    },
    home: {
      eyebrow: "Metall list, po‘lat list va yuqori unumli po‘latlar",
      title: "Murakkab qo‘llanishlar uchun texnik po‘lat yechimlari",
      intro:
        "Yuqori mustahkam, yeyilishga chidamli, zirhli va himoya po‘lat mahsulotlarini tekshirilgan texnik ma’lumot hamda aniq bog‘langan hujjatlar orqali ko‘ring.",
      primaryCta: "Mahsulotlarni ko‘rish",
      secondaryCta: "Taklif so‘rash",
      familiesTitle: "Mahsulot oilalari",
      familiesIntro:
        "Mahsulotlarni po‘lat oilasi, qo‘llanish sohasi va mavjud tekshirilgan xususiyatlar bo‘yicha toping.",
      featuredTitle: "Tanlangan mahsulotlar",
      featuredIntro:
        "Tanlangan markalar va manba hujjatlariga bog‘langan texnik ma’lumotlarni ko‘ring.",
      industriesTitle: "Tarmoqlar va qo‘llanishlar",
      industriesIntro:
        "Po‘lat variantlarini ish muhiti va muhandislik talabi bo‘yicha tanlang.",
      supportTitle: "Texnik yordam va xizmatlar",
      supportText:
        "Mahsulot tanlash, hujjatlardan foydalanish va qo‘llanish savollari bo‘yicha yordam so‘rang.",
      qualityTitle: "Sifat, sertifikatlar va standartlar",
      qualityText:
        "Faqat tasdiqlangan manba hujjatlari bilan isbotlangan standart va sertifikatlar ko‘rsatiladi.",
      documentsTitle: "Texnik hujjatlar markazi",
      documentsText:
        "Mavjud texnik ma’lumotlar varaqlari, qo‘llanmalar, risolalar va sertifikatlarni mahsulot hamda til bo‘yicha qidiring.",
      aboutTitle: "Metal Asia haqida",
      aboutText:
        "METAL ASIA — temir va po‘lat mahsulotlari yetkazib beruvchisi va BISALLOY® kompaniyasining rasmiy distribyutori.",
      contactTitle: "Talabingizni muhokama qilaylik",
      contactText:
        "Malakali javob uchun mahsulot, qalinlik, miqdor va qo‘llanish haqidagi zarur ma’lumotni yuboring.",
    },
    about: {
      title: "Metal Asia haqida",
      intro:
        "METAL ASIA temir va po‘lat mahsulotlarini sotish va yetkazib berish bilan shug‘ullanadi. Kompaniya BISALLOY® kompaniyasining rasmiy distribyutori hisoblanadi.",
      positioningTitle: "Texnik aniqlikka asoslangan",
      positioningText:
        "Mahsulotga oid bayonotlar manba hujjatlariga bog‘lanadi, noma’lum qiymatlar esa taxmin qilinmaydi va ochiq tarzda berilmagan deb belgilanadi.",
      principlesTitle: "Axborot bilan ishlash tamoyillarimiz",
      accuracyTitle: "Aniqlik",
      accuracyText:
        "Texnik qiymatlar faqat tasdiqlangan manbagacha kuzatilgandagina e’lon qilinadi.",
      traceabilityTitle: "Kuzatuvchanlik",
      traceabilityText:
        "Mahsulot yozuvlarida tasdiqlovchi hujjat va so‘nggi tekshiruv holati ko‘rsatiladi.",
      supportTitle: "Maqsadli yordam",
      supportText:
        "So‘rovlar texnik yoki tijorat murojaatini to‘g‘ri yo‘naltirish uchun zarur qo‘llanish ma’lumotlarini yig‘adi.",
      roleDisclosure:
        "METAL ASIA temir va po‘lat mahsulotlarini sotadi va yetkazib beradi, BISALLOY® mahsulotlarini esa rasmiy distribyutor sifatida taqdim etadi.",
    },
    products: {
      title: "Barcha mahsulotlar",
      intro:
        "Mavjud bo‘lmagan texnik qiymatlarni nol deb qabul qilmasdan maxsus po‘lat markalarini qidiring, saralang va solishtiring.",
      searchLabel: "Mahsulotlarni qidirish",
      searchPlaceholder: "Mahsulot, oila yoki qo‘llanish bo‘yicha qidirish",
      resultCount: "{count} ta mahsulot",
      familyFilter: "Mahsulot oilasi",
      applicationFilter: "Qo‘llanish sohasi",
      hardnessFilter: "Brinell bo‘yicha qattiqlik",
      thicknessFilter: "Qalinlik",
      yieldStrengthFilter: "Oquvchanlik chegarasi",
      tensileStrengthFilter: "Cho‘zilishdagi mustahkamlik",
      viewLabel: "Natijalar ko‘rinishi",
      gridView: "Kartochka ko‘rinishi",
      listView: "Ro‘yxat ko‘rinishi",
      activeFilters: "Faol filtrlar",
      clearFilters: "Barcha filtrlarni tozalash",
      compare: "Solishtirish",
      addToCompare: "Solishtirishga qo‘shish",
      removeFromCompare: "Solishtirishdan olib tashlash",
      compareTitle: "Mahsulotlarni solishtirish",
      compareHint: "Ikkitadan to‘rttagacha mahsulot tanlang.",
      compareMinimum: "Solishtirish uchun kamida ikkita mahsulot tanlang.",
      compareMaximum: "Ko‘pi bilan to‘rtta mahsulotni solishtirish mumkin.",
      compareNow: "Tanlangan mahsulotlarni solishtirish",
      emptyTitle: "Mos mahsulot topilmadi",
      emptyText: "Qidiruvni o‘zgartiring yoki bir yoxud bir nechta filtrni tozalang.",
      missingNumericData:
        "Tekshirilgan qiymati yo‘q mahsulotlar ushbu raqamli filtrga kiritilmaydi; yetishmayotgan ma’lumot nol deb hisoblanmaydi.",
      viewDetails: "Batafsil ko‘rish",
      requestDocument: "Texnik hujjat so‘rash",
      downloadDatasheet: "Texnik ma’lumotlar varag‘ini yuklash",
    },
    product: {
      overview: "Umumiy ma’lumot",
      gallery: "Mahsulot tasvirlari",
      advantages: "Asosiy afzalliklar",
      applications: "Qo‘llanish sohalari",
      technicalSpecifications: "Texnik xususiyatlar",
      thicknessRange: "Qalinlik oralig‘i",
      brinellHardness: "Brinell bo‘yicha qattiqlik",
      yieldStrength: "Oquvchanlik chegarasi",
      proofStrength: "Shartli oquvchanlik chegarasi",
      tensileStrength: "Cho‘zilishdagi mustahkamlik",
      chemicalComposition: "Kimyoviy tarkib",
      mechanicalProperties: "Mexanik xususiyatlar",
      equivalentStandards: "Muqobil standartlar",
      certifications: "Tekshirilgan sertifikatlar",
      processingNotes: "Kesish, payvandlash va bukish bo‘yicha izohlar",
      cutting: "Kesish",
      welding: "Payvandlash",
      bending: "Bukish",
      relatedProducts: "Tegishli mahsulotlar",
      documentModule: "Texnik ma’lumotlar varaqlari va qo‘shimcha hujjatlar",
      dataSource: "Manba hujjati",
      lastVerified: "So‘nggi tekshiruv",
      missingValue: "Tekshirilgan qiymat mavjud emas",
      missingDataNotice:
        "Faqat rasmiy hujjatlar bilan tasdiqlangan maydonlar ko‘rsatiladi. Berilmagan qiymatlar taxmin qilinmagan.",
      noDocumentTitle: "Texnik hujjat mavjud emas",
      noDocumentText:
        "Ushbu mahsulotga hozircha tasdiqlangan hujjat bog‘lanmagan. Uni texnik guruhdan so‘rashingiz mumkin.",
      requestDocument: "Ushbu hujjatni so‘rash",
      documentAvailableIn: "Ushbu hujjat {language} tilida mavjud.",
      selectedLanguageUnavailable:
        "Tanlangan tilda texnik hujjat yo‘q. Mavjud hujjatlar o‘z tili ko‘rsatilgan holda beriladi.",
    },
    industries: {
      title: "Tarmoqlar",
      intro:
        "Maxsus po‘lat ma’lumotlarini qo‘llanish muhiti bo‘yicha ko‘ring. Mahsulot mosligi tekshirilgan texnik talablar asosida tasdiqlanishi kerak.",
      exploreSector: "Ushbu tarmoqni ko‘rish",
      sectors: {
        defense: {
          title: "Mudofaa va xavfsizlik",
          description:
            "Ballistik va himoya muhandislik baholari uchun hujjatlashtirilgan zirhli hamda himoya po‘lat markalarini ko‘ring.",
        },
        mining: {
          title: "Konchilik va qazish ishlari",
          description:
            "Kuchli yeyilish yuz beradigan qazib olish va yer ishlariga mo‘ljallangan yeyilishga chidamli, yuqori mustahkam markalarni ko‘ring.",
        },
        mineralProcessing: {
          title: "Minerallarni qayta ishlash",
          description:
            "Abraziv material oqimiga duch keladigan novlar, qoplamalar, bunkerlar va boshqa qismlar uchun hujjatlashtirilgan variantlarni toping.",
        },
        construction: {
          title: "Qurilish",
          description:
            "Murakkab konstruksiya va maydon uskunalari uchun yuqori mustahkam hamda yeyilishga chidamli markalarni solishtiring.",
        },
        transportation: {
          title: "Transport",
          description:
            "Og‘irlikka sezgir, yuk ko‘taruvchi va yeyilishga duch keladigan transport qo‘llanishlari uchun po‘lat markalarini baholang.",
        },
        energy: {
          title: "Energetika",
          description:
            "Murakkab xizmat sharoitidagi uskuna va infratuzilma uchun tekshirilgan po‘lat ma’lumotlarini ko‘ring.",
        },
        agriculture: {
          title: "Qishloq xo‘jaligi",
          description:
            "Tuproq bilan ishlaydigan va material tashiydigan qishloq xo‘jaligi qismlari uchun yeyilishga chidamli markalarni ko‘ring.",
        },
        engineering: {
          title: "Umumiy mashinasozlik va tayyorlash",
          description:
            "Muhandislik qismlari, mashinalar va tayyorlashni rejalash uchun hujjatlashtirilgan xususiyatlarni solishtiring.",
        },
      },
    },
    documents: {
      title: "Texnik hujjatlar markazi",
      intro:
        "Tasdiqlangan texnik ma’lumotlar varaqlari, qo‘llanmalar, risolalar va sertifikatlarni mahsulot, oila, tur, til va yil bo‘yicha qidiring.",
      searchLabel: "Hujjatlarni qidirish",
      searchPlaceholder: "Hujjat yoki mahsulot nomi bo‘yicha qidirish",
      familyFilter: "Mahsulot oilasi",
      productFilter: "Mahsulot",
      typeFilter: "Hujjat turi",
      languageFilter: "Hujjat tili",
      yearFilter: "Nashr yili",
      datasheet: "Texnik ma’lumotlar varag‘i",
      technicalGuide: "Texnik qo‘llanma",
      brochure: "Risola",
      certificate: "Sertifikat",
      documentLanguage: "Hujjat tili",
      version: "Tahrir",
      publicationDate: "Nashr sanasi",
      fileSize: "Fayl hajmi",
      lastUpdated: "So‘nggi yangilanish",
      source: "Manba",
      familyDatasheet: "Mahsulot oilasining texnik ma’lumotlar varag‘i",
      selectedLanguageMissing:
        "Tanlangan interfeys tilida hujjat yo‘q; mavjud hujjatlar haqiqiy tili bilan ko‘rsatiladi.",
      preview: "Hujjatni oldindan ko‘rish",
      previewUnavailable:
        "Bu qurilmada oldindan ko‘rish qo‘llab-quvvatlanmaydi. Hujjatni yangi varaqda oching yoki yuklab oling.",
      emptyTitle: "Mos hujjat topilmadi",
      emptyText: "Qidiruvni o‘zgartiring yoki bir yoxud bir nechta hujjat filtrini tozalang.",
    },
    quality: {
      title: "Sifat, sertifikatlar va standartlar",
      intro:
        "Faqat tasdiqlangan hujjatlar bilan isbotlangan sifat yozuvlari, standartlar va sertifikatlarni ko‘ring.",
      verifiedOnlyTitle: "Faqat tekshirilgan ma’lumot",
      verifiedOnlyText:
        "Standart yoki sertifikat uning manbasi va mahsulotga aloqasi tasdiqlangandan keyingina ko‘rsatiladi.",
      certificatesTitle: "Sertifikatlar",
      noCertificatesTitle: "Tasdiqlangan sertifikatlar e’lon qilinmagan",
      noCertificatesText:
        "Tegishli fayllar va ularning amal doirasi tekshirilgandan keyin sertifikatlar shu yerda ko‘rsatiladi.",
      standardsTitle: "Standartlar",
      traceabilityTitle: "Hujjatlar kuzatuvchanligi",
      traceabilityText:
        "E’lon qilingan yozuvlarda ma’lum bo‘lsa, manba, til, tahrir, nashr sanasi va tekshiruv holati saqlanadi.",
    },
    services: {
      title: "Texnik yordam va xizmatlar",
      intro:
        "Mahsulot tanlash, texnik hujjat yoki qo‘llanishga oid ma’lumot uchun tuzilmali so‘rov yuboring.",
      selectionTitle: "Mahsulot tanlashda yordam",
      selectionText:
        "Hujjatlashtirilgan xususiyatlarni solishtiring va keyingi baholash uchun qo‘llanish talablaringizni yuboring.",
      documentsTitle: "Hujjatlar bo‘yicha yordam",
      documentsText:
        "Mavjud bo‘lmagan texnik ma’lumotlar varag‘ini so‘rang yoki mavjud hujjatning tili va tahririni aniqlashtiring.",
      processingTitle: "Ishlov berish ma’lumoti",
      processingText:
        "Kesish, payvandlash va bukish bo‘yicha ko‘rsatmalar faqat rasmiy mahsulot hujjatlari bilan tasdiqlanganda beriladi.",
      enquiryTitle: "Texnik so‘rov",
      enquiryText:
        "So‘rovni aniq baholash uchun marka, qalinlik, miqdor va ish sharoitlarini ko‘rsating.",
    },
    contact: {
      title: "Aloqa va taklif so‘rash",
      intro:
        "Baholayotgan mahsulotingiz va texnik talabingizni bildiring.",
      detailsTitle: "Aloqa ma’lumotlari",
      detailsUnavailable:
        "Tekshirilgan elektron pochta, telefon, WhatsApp va manzil ma’lumotlari hozircha nashrga sozlanmagan.",
      quoteTitle: "Taklif so‘rash",
      quoteText:
        "Malakali javob uchun mahsulot, qalinlik, miqdor va qo‘llanish ma’lumotini bering.",
    },
    form: {
      fullName: "Ism va familiya",
      company: "Kompaniya",
      email: "Elektron pochta",
      phone: "Telefon",
      country: "Mamlakat",
      city: "Shahar",
      address: "Manzil",
      product: "Qiziqtirgan mahsulot",
      thickness: "Kerakli qalinlik",
      quantity: "Miqdor",
      message: "Xabar",
      privacyConsent: "So‘rovim qanday qayta ishlanishi haqidagi maxfiylik bildirishnomasini o‘qidim.",
      privacyLinkText: "Maxfiylik siyosatini o‘qish",
      selectPlaceholder: "Variantni tanlang",
      optional: "Ixtiyoriy",
      submit: "So‘rovni yuborish",
      submitting: "So‘rov yuborilmoqda",
      required: "Bu maydon majburiy.",
      invalidEmail: "To‘g‘ri elektron pochta manzilini kiriting.",
      invalidPhone: "To‘g‘ri telefon raqamini kiriting.",
      errorSummaryTitle: "Quyidagi maydonlarni tuzating",
      spamDetected: "So‘rovni tekshirib bo‘lmadi. Qayta urinib ko‘ring.",
      successTitle: "So‘rovingiz qabul qilindi",
      successText:
        "Sozlangan xizmat so‘rovingiz olinganini tasdiqladi. Keyingi aloqa uchun ko‘rsatilgan raqamni saqlang.",
      errorTitle: "So‘rovingiz yuborilmadi",
      errorText:
        "Xizmat so‘rovni qabul qila olmadi. Maydonlarni tekshirib, keyinroq qayta urinib ko‘ring.",
      integrationMissingTitle: "Elektron pochta orqali yuborish ishlatiladi",
      integrationMissingText:
        "Server orqali yuborish hali ulanmagan. Tekshiruvdan so‘ng so‘rov elektron pochta dasturingizda tayyor xabar sifatida ochiladi va faqat u yerda tasdiqlaganingizdan keyin yuboriladi.",
    },
    legal: {
      privacyTitle: "Maxfiylik siyosati",
      privacyBlockerTitle: "Tasdiqlangan maxfiylik matni zarur",
      privacyBlockerText:
        "Yuridik shaxs, ma’lumotlar operatori, qayta ishlash maqsadlari, saqlash muddatlari va aloqa ma’lumotlari tasdiqlanmaguncha maxfiylik siyosati e’lon qilinmaydi.",
      cookiesTitle: "Cookie fayllari siyosati",
      cookiesBlockerTitle: "Cookie fayllari bo‘yicha tasdiqlangan matn zarur",
      cookiesBlockerText:
        "Ishlatiladigan fayllar, ularning maqsadlari, muddatlari, ta’minotchilari va rozilik jarayoni tasdiqlanmaguncha siyosat e’lon qilinmaydi.",
      preferenceStorageText:
        "Majburiy til tanlovini saqlash tahlil va marketing roziligidan alohida yuritiladi.",
      legalReviewRequired: "E’lon qilishdan oldin huquqiy tekshiruv zarur.",
    },
    breadcrumbs: {
      label: "Sahifa yo‘li",
      home: "Bosh sahifa",
      currentPage: "Joriy sahifa",
    },
    notFound: {
      title: "Sahifa topilmadi",
      text:
        "Manzil o‘zgargan bo‘lishi yoki bu sahifa tanlangan tilda mavjud bo‘lmasligi mumkin.",
      backHome: "Bosh sahifaga qaytish",
      browseProducts: "Barcha mahsulotlarni ko‘rish",
    },
    footer: {
      tagline:
        "Maxsus po‘latni baholash uchun tekshirilgan mahsulot ma’lumoti va texnik hujjatlar.",
      productLinksTitle: "Mahsulotlar",
      companyLinksTitle: "Kompaniya",
      resourceLinksTitle: "Manbalar",
      sitemapLabel: "XML sayt xaritasi",
      contactTitle: "Aloqa",
      rights: "Barcha huquqlar himoyalangan.",
      legalEntityMissing: "Tekshirilgan yuridik shaxs ma’lumotlari e’lon qilinishini kutmoqda.",
      contactDetailsMissing: "Tekshirilgan aloqa ma’lumotlari e’lon qilinishini kutmoqda.",
    },
    seo: {
      home: {
        title: "Po‘lat list va metall list | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA BISALLOY® po‘lat listi, metall list, yeyilishga chidamli list, yuqori mustahkam konstruksion va zirhli po‘latni texnik hujjatlar bilan yetkazib beradi.",
      },
      about: {
        title: "Metal Asia haqida",
        description:
          "METAL ASIA — temir va po‘lat mahsulotlari sotuvchisi va yetkazib beruvchisi, shuningdek, BISALLOY® kompaniyasining rasmiy distribyutori.",
      },
      products: {
        title: "Po‘lat list va yeyilishga chidamli po‘lat | METAL ASIA",
        description:
          "Yuqori mustahkam, yeyilishga chidamli, zirhli va himoya po‘lat markalarini qidiring, saralang va solishtiring.",
      },
      industries: {
        title: "Tarmoqlar va qo‘llanishlar | Metal Asia",
        description:
          "Hujjatlashtirilgan maxsus po‘lat variantlarini tarmoq, ish muhiti va muhandislik talabi bo‘yicha ko‘ring.",
      },
      documents: {
        title: "Texnik hujjatlar | Metal Asia",
        description:
          "Mavjud po‘lat ma’lumotlar varaqlari, texnik qo‘llanmalar, risolalar va sertifikatlarni mahsulot hamda til bo‘yicha qidiring.",
      },
      quality: {
        title: "Sifat, sertifikatlar va standartlar | Metal Asia",
        description:
          "Tasdiqlangan manba hujjatlariga bog‘langan tekshirilgan sifat yozuvlari, standartlar va sertifikatlarni ko‘ring.",
      },
      services: {
        title: "Texnik yordam va xizmatlar | Metal Asia",
        description:
          "Mahsulot tanlash yordami, texnik hujjatlar va qo‘llanishga oid po‘lat ma’lumotini so‘rang.",
      },
      contact: {
        title: "Aloqa va taklif so‘rash | Metal Asia",
        description:
          "Metal Asia kompaniyasiga mahsulot, qalinlik, miqdor va texnik talablaringizni yuboring.",
      },
      privacy: {
        title: "Maxfiylik siyosati | Metal Asia",
        description:
          "Metal Asia kompaniyasining tasdiqlangan maxfiylik va ma’lumotlarni qayta ishlash axborotini o‘qing.",
      },
      cookies: {
        title: "Cookie fayllari siyosati | Metal Asia",
        description:
          "Metal Asia majburiy va rozilikka asoslangan brauzer saqlovidan qanday foydalanishini o‘qing.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "{product} uchun tekshirilgan xususiyatlar, qo‘llanishlar va mavjud texnik hujjatlarni ko‘ring.",
      },
      industryDetail: {
        titleTemplate: "{industry} uchun po‘lat qo‘llanishlari | Metal Asia",
        descriptionTemplate:
          "{industry} uchun hujjatlashtirilgan maxsus po‘lat mahsulotlari va texnik manbalarni ko‘ring.",
      },
    },
    accessibility: {
      skipToContent: "Asosiy mazmunga o‘tish",
      mainNavigation: "Asosiy navigatsiya",
      languageSelector: "Til tanlash",
      selectLanguage: "Tilni tanlang",
      openMenu: "Menyuni ochish",
      closeMenu: "Menyuni yopish",
      productSearch: "Mahsulotlar katalogidan qidirish",
      filters: "Mahsulot filtrlari",
      removeFilter: "{filter} filtrini olib tashlash",
      gridView: "Mahsulotlarni kartochka shaklida ko‘rsatish",
      listView: "Mahsulotlarni ro‘yxat shaklida ko‘rsatish",
      compareSelection: "Solishtirish uchun {count} ta mahsulot tanlandi",
      documentPreview: "Texnik hujjatni oldindan ko‘rish",
      previousImage: "Oldingi tasvirni ko‘rsatish",
      nextImage: "Keyingi tasvirni ko‘rsatish",
      closeDialog: "Muloqot oynasini yopish",
      externalLink: "Tashqi havola",
      opensNewTab: "Yangi varaqda ochiladi",
      requiredField: "Majburiy maydon",
      errorSummary: "Shakl xatolari jamlanmasi",
      loading: "Mazmun yuklanmoqda",
    },
  },
  ky: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Өнүмдөрдү көрүү",
      requestQuote: "Сунуш суроо",
      requestTechnicalInfo: "Техникалык маалымат суроо",
      learnMore: "Кененирээк",
      viewAll: "Баарын көрүү",
      download: "Жүктөп алуу",
      openInNewTab: "Жаңы өтмөктө ачуу",
      search: "Издөө",
      clear: "Тазалоо",
      close: "Жабуу",
      back: "Артка",
      next: "Кийинки",
      previous: "Мурунку",
      loading: "Жүктөлүүдө",
      unknown: "Белгисиз",
      notAvailable: "Жеткиликсиз",
      language: "Тил",
      menu: "Меню",
    },
    nav: {
      home: "Башкы бет",
      about: "Компания жөнүндө",
      products: "Өнүмдөр",
      industries: "Тармактар",
      documents: "Техникалык документтер",
      quality: "Сапат жана стандарттар",
      services: "Техникалык колдоо",
      contact: "Байланыш",
      wear: "Эскиришди салыштыруу",
    },
    home: {
      eyebrow: "Металл табак, болот лист жана жогорку натыйжалуу болоттор",
      title: "Татаал колдонмолор үчүн техникалык болот чечимдери",
      intro:
        "Жогорку бекемдиктеги, эскирүүгө туруктуу, соот жана коргоочу болот өнүмдөрүн текшерилген техникалык маалымат жана так байланыштырылган документтер аркылуу көрүңүз.",
      primaryCta: "Өнүмдөрдү көрүү",
      secondaryCta: "Сунуш суроо",
      familiesTitle: "Өнүмдөр топтору",
      familiesIntro:
        "Өнүмдөрдү болот тобу, колдонуу тармагы жана жеткиликтүү текшерилген касиеттери боюнча табыңыз.",
      featuredTitle: "Тандалган өнүмдөр",
      featuredIntro:
        "Тандалган маркаларды жана баштапкы документтер менен байланышкан техникалык маалыматты көрүңүз.",
      industriesTitle: "Тармактар жана колдонмолор",
      industriesIntro:
        "Болот варианттарын иштөө чөйрөсү жана инженердик талап боюнча тандаңыз.",
      supportTitle: "Техникалык колдоо жана кызматтар",
      supportText:
        "Өнүм тандоо, документ алуу жана колдонуу суроолору боюнча жардам сураңыз.",
      qualityTitle: "Сапат, сертификаттар жана стандарттар",
      qualityText:
        "Бекитилген баштапкы документтер менен ырасталган стандарттар жана сертификаттар гана көрсөтүлөт.",
      documentsTitle: "Техникалык документтер борбору",
      documentsText:
        "Жеткиликтүү техникалык маалымат барактарын, колдонмолорду, брошюраларды жана сертификаттарды өнүм жана тил боюнча издеңиз.",
      aboutTitle: "Metal Asia жөнүндө",
      aboutText:
        "METAL ASIA — темир жана болот продукцияларын жеткирүүчү жана BISALLOY® компаниясынын расмий дистрибьютору.",
      contactTitle: "Талабыңызды талкуулайлы",
      contactText:
        "Так жооп алуу үчүн өнүм, калыңдык, көлөм жана колдонуу жөнүндө керектүү маалыматты жөнөтүңүз.",
    },
    about: {
      title: "Metal Asia жөнүндө",
      intro:
        "METAL ASIA темир жана болот продукцияларын сатуу жана жеткирүү менен алектенет. Компания BISALLOY® компаниясынын расмий дистрибьютору болуп саналат.",
      positioningTitle: "Техникалык тактыкка негизделген",
      positioningText:
        "Өнүм боюнча билдирүүлөр баштапкы документтерге байланыштырылат, ал эми белгисиз маанилер болжолдонбой, маалымат жок деп ачык көрсөтүлөт.",
      principlesTitle: "Маалымат менен иштөө принциптерибиз",
      accuracyTitle: "Тактык",
      accuracyText:
        "Техникалык маанилер бекитилген булакка чейин көзөмөлдөнгөндө гана жарыяланат.",
      traceabilityTitle: "Көзөмөлдөнүүчүлүк",
      traceabilityText:
        "Өнүм жазууларында ырастоочу документ жана акыркы текшерүү абалы көрсөтүлөт.",
      supportTitle: "Максаттуу колдоо",
      supportText:
        "Сурамдар техникалык же коммерциялык кайрылууну туура багыттоого зарыл болгон колдонуу маалыматын чогултат.",
      roleDisclosure:
        "METAL ASIA темир жана болот продукцияларын сатат жана жеткирет, ошондой эле BISALLOY® продукцияларын расмий дистрибьютор катары сунуштайт.",
    },
    products: {
      title: "Бардык өнүмдөр",
      intro:
        "Жок техникалык маанилерди нөл деп эсептебестен, атайын болот маркаларын издеңиз, чыпкалаңыз жана салыштырыңыз.",
      searchLabel: "Өнүмдөрдү издөө",
      searchPlaceholder: "Өнүм, топ же колдонуу тармагы боюнча издөө",
      resultCount: "{count} өнүм",
      familyFilter: "Өнүмдөр тобу",
      applicationFilter: "Колдонуу тармагы",
      hardnessFilter: "Бринелль боюнча катуулук",
      thicknessFilter: "Калыңдык",
      yieldStrengthFilter: "Агуу чеги",
      tensileStrengthFilter: "Созулуудагы бекемдик",
      viewLabel: "Натыйжалардын көрүнүшү",
      gridView: "Карточкалык көрүнүш",
      listView: "Тизмелик көрүнүш",
      activeFilters: "Активдүү чыпкалар",
      clearFilters: "Бардык чыпкаларды тазалоо",
      compare: "Салыштыруу",
      addToCompare: "Салыштырууга кошуу",
      removeFromCompare: "Салыштыруудан чыгаруу",
      compareTitle: "Өнүмдөрдү салыштыруу",
      compareHint: "Экиден төрткө чейин өнүм тандаңыз.",
      compareMinimum: "Салыштыруу үчүн кеминде эки өнүм тандаңыз.",
      compareMaximum: "Төрткө чейин өнүмдү салыштырууга болот.",
      compareNow: "Тандалган өнүмдөрдү салыштыруу",
      emptyTitle: "Туура келген өнүм табылган жок",
      emptyText: "Издөөнү өзгөртүңүз же бир нече чыпканы тазалаңыз.",
      missingNumericData:
        "Текшерилген мааниси жок өнүмдөр бул сандык чыпкага кирбейт; жетишпеген маалымат нөл деп эсептелбейт.",
      viewDetails: "Толук маалымат",
      requestDocument: "Техникалык документ суроо",
      downloadDatasheet: "Техникалык маалымат барагын жүктөө",
    },
    product: {
      overview: "Жалпы маалымат",
      gallery: "Өнүм сүрөттөрү",
      advantages: "Негизги артыкчылыктар",
      applications: "Колдонуу тармактары",
      technicalSpecifications: "Техникалык мүнөздөмөлөр",
      thicknessRange: "Калыңдык аралыгы",
      brinellHardness: "Бринелль боюнча катуулук",
      yieldStrength: "Агуу чеги",
      proofStrength: "Шарттуу агуу чеги",
      tensileStrength: "Созулуудагы бекемдик",
      chemicalComposition: "Химиялык курам",
      mechanicalProperties: "Механикалык касиеттер",
      equivalentStandards: "Барабар стандарттар",
      certifications: "Текшерилген сертификаттар",
      processingNotes: "Кесүү, ширетүү жана ийүү боюнча эскертмелер",
      cutting: "Кесүү",
      welding: "Ширетүү",
      bending: "Ийүү",
      relatedProducts: "Байланыштуу өнүмдөр",
      documentModule: "Техникалык маалымат барактары жана кошумча документтер",
      dataSource: "Баштапкы документ",
      lastVerified: "Акыркы текшерүү",
      missingValue: "Текшерилген маани жеткиликсиз",
      missingDataNotice:
        "Расмий документтер менен ырасталган талаалар гана көрсөтүлөт. Көрсөтүлбөгөн маанилер болжолдонгон эмес.",
      noDocumentTitle: "Техникалык документ жеткиликсиз",
      noDocumentText:
        "Бул өнүмгө азырынча бекитилген документ байланышкан эмес. Аны техникалык топтон сурасаңыз болот.",
      requestDocument: "Бул документти суроо",
      documentAvailableIn: "Бул документ {language} тилинде жеткиликтүү.",
      selectedLanguageUnavailable:
        "Тандалган тилде техникалык документ жок. Жеткиликтүү документтер өз тили көрсөтүлүп берилет.",
    },
    industries: {
      title: "Тармактар",
      intro:
        "Атайын болот тууралуу маалыматты колдонуу чөйрөсүнө жараша көрүңүз. Өнүмдүн жарамдуулугу текшерилген техникалык талаптарга ылайык ырасталышы керек.",
      exploreSector: "Бул тармакты көрүү",
      sectors: {
        defense: {
          title: "Коргоо жана коопсуздук",
          description:
            "Баллистикалык жана коргоочу инженердик баалоо үчүн документтештирилген соот жана коргоочу болот маркаларын көрүңүз.",
        },
        mining: {
          title: "Тоо-кен иштери жана жер казуу",
          description:
            "Катуу эскирүүчү казып алуу жана жер иштерине арналган эскирүүгө туруктуу, жогорку бекемдиктеги маркаларды көрүңүз.",
        },
        mineralProcessing: {
          title: "Минералдарды иштетүү",
          description:
            "Абразивдүү материал агымына туш болгон ноолор, каптамалар, бункерлер жана башка бөлүктөр үчүн документтештирилген варианттарды табыңыз.",
        },
        construction: {
          title: "Курулуш",
          description:
            "Татаал курулуштар жана аянт жабдуулары үчүн жогорку бекемдиктеги жана эскирүүгө туруктуу маркаларды салыштырыңыз.",
        },
        transportation: {
          title: "Транспорт",
          description:
            "Салмакка сезимтал, жүк көтөрүүчү жана эскирүүчү транспорттук колдонмолор үчүн болот маркаларын баалаңыз.",
        },
        energy: {
          title: "Энергетика",
          description:
            "Татаал кызмат шартындагы жабдуу жана инфраструктура үчүн текшерилген болот маалыматын көрүңүз.",
        },
        agriculture: {
          title: "Айыл чарба",
          description:
            "Топурак менен иштеген жана материал ташуучу айыл чарба бөлүктөрү үчүн эскирүүгө туруктуу маркаларды көрүңүз.",
        },
        engineering: {
          title: "Жалпы машина куруу жана даярдоо",
          description:
            "Инженердик бөлүктөр, машиналар жана даярдоону пландоо үчүн документтештирилген касиеттерди салыштырыңыз.",
        },
      },
    },
    documents: {
      title: "Техникалык документтер борбору",
      intro:
        "Бекитилген техникалык маалымат барактарын, колдонмолорду, брошюраларды жана сертификаттарды өнүм, топ, түр, тил жана жыл боюнча издеңиз.",
      searchLabel: "Документтерди издөө",
      searchPlaceholder: "Документ же өнүм аты боюнча издөө",
      familyFilter: "Өнүмдөр тобу",
      productFilter: "Өнүм",
      typeFilter: "Документтин түрү",
      languageFilter: "Документтин тили",
      yearFilter: "Жарыяланган жылы",
      datasheet: "Техникалык маалымат барагы",
      technicalGuide: "Техникалык колдонмо",
      brochure: "Брошюра",
      certificate: "Сертификат",
      documentLanguage: "Документтин тили",
      version: "Нуска",
      publicationDate: "Жарыяланган күнү",
      fileSize: "Файлдын көлөмү",
      lastUpdated: "Акыркы жаңыртуу",
      source: "Булак",
      familyDatasheet: "Өнүмдөр тобунун техникалык маалымат барагы",
      selectedLanguageMissing:
        "Тандалган интерфейс тилинде документ жок; жеткиликтүү документтер өзүнүн чыныгы тили менен көрсөтүлөт.",
      preview: "Документти алдын ала көрүү",
      previewUnavailable:
        "Бул түзмөктө алдын ала көрүү колдоого алынбайт. Документти жаңы өтмөктө ачыңыз же жүктөп алыңыз.",
      emptyTitle: "Туура келген документ табылган жок",
      emptyText: "Издөөнү өзгөртүңүз же бир нече документ чыпкасын тазалаңыз.",
    },
    quality: {
      title: "Сапат, сертификаттар жана стандарттар",
      intro:
        "Бекитилген документтер менен ырасталган сапат жазууларын, стандарттарды жана сертификаттарды гана көрүңүз.",
      verifiedOnlyTitle: "Текшерилген маалымат гана",
      verifiedOnlyText:
        "Стандарт же сертификат анын булагы жана өнүм менен байланышы ырасталгандан кийин гана көрсөтүлөт.",
      certificatesTitle: "Сертификаттар",
      noCertificatesTitle: "Бекитилген сертификаттар жарыяланган жок",
      noCertificatesText:
        "Тиешелүү файлдар жана алардын колдонуу чөйрөсү текшерилгенден кийин сертификаттар бул жерде көрсөтүлөт.",
      standardsTitle: "Стандарттар",
      traceabilityTitle: "Документтердин көзөмөлдөнүүчүлүгү",
      traceabilityText:
        "Жарыяланган жазууларда белгилүү болсо, булак, тил, нуска, жарыяланган күн жана текшерүү абалы сакталат.",
    },
    services: {
      title: "Техникалык колдоо жана кызматтар",
      intro:
        "Өнүм тандоо, техникалык документ же колдонмого байланыштуу маалымат үчүн түзүмдүү сурам жөнөтүңүз.",
      selectionTitle: "Өнүм тандоону колдоо",
      selectionText:
        "Документтештирилген касиеттерди салыштырып, андан аркы баалоо үчүн колдонуу талаптарыңызды жөнөтүңүз.",
      documentsTitle: "Документтик колдоо",
      documentsText:
        "Жеткиликсиз техникалык маалымат барагын сураңыз же бар документтин тили менен нускасын тактаңыз.",
      processingTitle: "Иштетүү маалыматы",
      processingText:
        "Кесүү, ширетүү жана ийүү боюнча көрсөтмөлөр расмий өнүм документтери менен ырасталганда гана берилет.",
      enquiryTitle: "Техникалык сурам",
      enquiryText:
        "Сурамды так баалоо үчүн марка, калыңдык, көлөм жана иштөө шарттарын көрсөтүңүз.",
    },
    contact: {
      title: "Байланыш жана сунуш суроо",
      intro:
        "Баалап жаткан өнүмүңүздү жана техникалык талабыңызды билдириңиз.",
      detailsTitle: "Байланыш маалыматтары",
      detailsUnavailable:
        "Текшерилген электрондук почта, телефон, WhatsApp жана дарек маалыматтары азырынча жарыялоого жөндөлгөн эмес.",
      quoteTitle: "Сунуш суроо",
      quoteText:
        "Так жооп алуу үчүн өнүм, калыңдык, көлөм жана колдонуу маалыматын бериңиз.",
    },
    form: {
      fullName: "Аты-жөнү",
      company: "Компания",
      email: "Электрондук почта",
      phone: "Телефон",
      country: "Өлкө",
      city: "Шаар",
      address: "Дарек",
      product: "Кызыктырган өнүм",
      thickness: "Керектүү калыңдык",
      quantity: "Көлөм",
      message: "Билдирүү",
      privacyConsent: "Өтүнүчүмдүн иштетилиши жөнүндө купуялуулук билдирүүсүн окудум.",
      privacyLinkText: "Купуялык саясатын окуу",
      selectPlaceholder: "Вариантты тандаңыз",
      optional: "Милдеттүү эмес",
      submit: "Сурам жөнөтүү",
      submitting: "Сурам жөнөтүлүүдө",
      required: "Бул талаа милдеттүү.",
      invalidEmail: "Жарактуу электрондук почта дарегин киргизиңиз.",
      invalidPhone: "Жарактуу телефон номерин киргизиңиз.",
      errorSummaryTitle: "Төмөнкү талааларды оңдоңуз",
      spamDetected: "Сурамды текшерүү мүмкүн болгон жок. Кайра аракет кылыңыз.",
      successTitle: "Сурамыңыз кабыл алынды",
      successText:
        "Жөндөлгөн кызмат сурамыңыздын алынганын ырастады. Кийин кайрылуу үчүн көрсөтүлгөн номерди сактаңыз.",
      errorTitle: "Сурамыңыз жөнөтүлгөн жок",
      errorText:
        "Кызмат сурамды кабыл ала алган жок. Талааларды текшерип, кийин кайра аракет кылыңыз.",
      integrationMissingTitle: "Электрондук почта аркылуу жөнөтүү колдонулат",
      integrationMissingText:
        "Сервер аркылуу жөнөтүү азырынча туташкан эмес. Текшерүүдөн кийин сурам электрондук почта колдонмоңузда даяр билдирүү катары ачылып, сиз ошол жерден ырастагандан кийин гана жөнөтүлөт.",
    },
    legal: {
      privacyTitle: "Купуялык саясаты",
      privacyBlockerTitle: "Бекитилген купуялык тексти керек",
      privacyBlockerText:
        "Юридикалык жак, маалымат оператору, иштетүү максаттары, сактоо мөөнөттөрү жана байланыш маалыматы бекитилмейинче купуялык саясаты жарыяланбайт.",
      cookiesTitle: "Cookie файлдарынын саясаты",
      cookiesBlockerTitle: "Cookie файлдары боюнча бекитилген текст керек",
      cookiesBlockerText:
        "Колдонулган файлдар, алардын максаттары, мөөнөттөрү, камсыздоочулары жана макулдук тартиби бекитилмейинче саясат жарыяланбайт.",
      preferenceStorageText:
        "Милдеттүү тил тандоосун сактоо талдоо жана маркетинг макулдугунан өзүнчө жүргүзүлөт.",
      legalReviewRequired: "Жарыялоодон мурун укуктук текшерүү керек.",
    },
    breadcrumbs: {
      label: "Багыт жолу",
      home: "Башкы бет",
      currentPage: "Учурдагы бет",
    },
    notFound: {
      title: "Бет табылган жок",
      text:
        "Дарек өзгөргөн болушу мүмкүн же бул бет тандалган тилде жеткиликсиз.",
      backHome: "Башкы бетке кайтуу",
      browseProducts: "Бардык өнүмдөрдү көрүү",
    },
    footer: {
      tagline:
        "Атайын болотту баалоо үчүн текшерилген өнүм маалыматы жана техникалык документтер.",
      productLinksTitle: "Өнүмдөр",
      companyLinksTitle: "Компания",
      resourceLinksTitle: "Маалыматтар",
      sitemapLabel: "XML сайт картасы",
      contactTitle: "Байланыш",
      rights: "Бардык укуктар корголгон.",
      legalEntityMissing: "Текшерилген юридикалык жак маалыматы жарыялоону күтүп жатат.",
      contactDetailsMissing: "Текшерилген байланыш маалыматы жарыялоону күтүп жатат.",
    },
    seo: {
      home: {
        title: "Болот табак жана металл лист | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA BISALLOY® болот табагын, металл листти, эскирүүгө туруктуу табакты, жогорку бекем конструкциялык жана соот болотту техникалык документтери менен камсыздайт.",
      },
      about: {
        title: "Metal Asia жөнүндө",
        description:
          "METAL ASIA — темир жана болот продукцияларын сатуучу жана жеткирүүчү, ошондой эле BISALLOY® компаниясынын расмий дистрибьютору.",
      },
      products: {
        title: "Болот табак жана эскирүүгө туруктуу болот | METAL ASIA",
        description:
          "Жогорку бекемдиктеги, эскирүүгө туруктуу, соот жана коргоочу болот маркаларын издеңиз, чыпкалаңыз жана салыштырыңыз.",
      },
      industries: {
        title: "Тармактар жана колдонмолор | Metal Asia",
        description:
          "Документтештирилген атайын болот варианттарын тармак, иштөө чөйрөсү жана инженердик талап боюнча көрүңүз.",
      },
      documents: {
        title: "Техникалык документтер | Metal Asia",
        description:
          "Жеткиликтүү болот маалымат барактарын, техникалык колдонмолорду, брошюраларды жана сертификаттарды өнүм жана тил боюнча издеңиз.",
      },
      quality: {
        title: "Сапат, сертификаттар жана стандарттар | Metal Asia",
        description:
          "Бекитилген баштапкы документтерге байланышкан текшерилген сапат жазууларын, стандарттарды жана сертификаттарды көрүңүз.",
      },
      services: {
        title: "Техникалык колдоо жана кызматтар | Metal Asia",
        description:
          "Өнүм тандоо колдоосун, техникалык документтерди жана колдонмого байланыштуу болот маалыматын сураңыз.",
      },
      contact: {
        title: "Байланыш жана сунуш суроо | Metal Asia",
        description:
          "Metal Asia компаниясына өнүм, калыңдык, көлөм жана техникалык талаптарыңызды жөнөтүңүз.",
      },
      privacy: {
        title: "Купуялык саясаты | Metal Asia",
        description:
          "Metal Asia компаниясынын бекитилген купуялык жана маалымат иштетүү шарттарын окуңуз.",
      },
      cookies: {
        title: "Cookie файлдарынын саясаты | Metal Asia",
        description:
          "Metal Asia милдеттүү жана макулдукка негизделген браузер сактагычын кантип колдоно турганын окуңуз.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "{product} үчүн текшерилген касиеттерди, колдонуу тармактарын жана жеткиликтүү техникалык документтерди көрүңүз.",
      },
      industryDetail: {
        titleTemplate: "{industry} үчүн болот колдонмолору | Metal Asia",
        descriptionTemplate:
          "{industry} үчүн документтештирилген атайын болот өнүмдөрүн жана техникалык маалыматтарды көрүңүз.",
      },
    },
    accessibility: {
      skipToContent: "Негизги мазмунга өтүү",
      mainNavigation: "Негизги багыттоо",
      languageSelector: "Тил тандоо",
      selectLanguage: "Тилди тандаңыз",
      openMenu: "Менюну ачуу",
      closeMenu: "Менюну жабуу",
      productSearch: "Өнүмдөр каталогунан издөө",
      filters: "Өнүм чыпкалары",
      removeFilter: "{filter} чыпкасын алып салуу",
      gridView: "Өнүмдөрдү карточка түрүндө көрсөтүү",
      listView: "Өнүмдөрдү тизме түрүндө көрсөтүү",
      compareSelection: "Салыштыруу үчүн {count} өнүм тандалды",
      documentPreview: "Техникалык документти алдын ала көрүү",
      previousImage: "Мурунку сүрөттү көрсөтүү",
      nextImage: "Кийинки сүрөттү көрсөтүү",
      closeDialog: "Сүйлөшүү терезесин жабуу",
      externalLink: "Тышкы шилтеме",
      opensNewTab: "Жаңы өтмөктө ачылат",
      requiredField: "Милдеттүү талаа",
      errorSummary: "Форма каталарынын жыйындысы",
      loading: "Мазмун жүктөлүүдө",
    },
  },
  kk: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Өнімдерді қарау",
      requestQuote: "Баға ұсынысын сұрау",
      requestTechnicalInfo: "Техникалық ақпарат сұрау",
      learnMore: "Толығырақ",
      viewAll: "Барлығын көру",
      download: "Жүктеп алу",
      openInNewTab: "Жаңа қойындыда ашу",
      search: "Іздеу",
      clear: "Тазалау",
      close: "Жабу",
      back: "Артқа",
      next: "Келесі",
      previous: "Алдыңғы",
      loading: "Жүктелуде",
      unknown: "Белгісіз",
      notAvailable: "Қолжетімсіз",
      language: "Тіл",
      menu: "Мәзір",
    },
    nav: {
      home: "Басты бет",
      about: "Компания туралы",
      products: "Өнімдер",
      industries: "Салалар",
      documents: "Техникалық құжаттар",
      quality: "Сапа және стандарттар",
      services: "Техникалық қолдау",
      contact: "Байланыс",
      wear: "Тозу салыстыруы",
    },
    home: {
      eyebrow: "Металл табақ, болат қаңылтыр және жоғары өнімді болаттар",
      title: "Күрделі қолдану жағдайларына арналған техникалық болат шешімдері",
      intro:
        "BISALLOY® және BISPLATE өнімдерін — тозуға төзімді болат, тозуға төзімді табақ, сауыт пен сауытты табақ, футеровка және wear liners шешімдерін — тексерілген техникалық ақпаратпен қараңыз.",
      primaryCta: "Өнімдерді қарау",
      secondaryCta: "Баға ұсынысын сұрау",
      familiesTitle: "Өнім топтары",
      familiesIntro:
        "Өнімдерді болат тобы, қолдану саласы және қолжетімді тексерілген қасиеттері бойынша табыңыз.",
      featuredTitle: "Таңдаулы өнімдер",
      featuredIntro:
        "Таңдалған маркаларды және бастапқы құжаттармен байланыстырылған техникалық ақпаратты қараңыз.",
      industriesTitle: "Салалар және қолдану бағыттары",
      industriesIntro:
        "Болат нұсқаларын жұмыс ортасы мен инженерлік талаптарға қарай таңдаңыз.",
      supportTitle: "Техникалық қолдау және қызметтер",
      supportText:
        "Өнім таңдау, құжатқа қол жеткізу және қолдану мәселелері бойынша көмек сұраңыз.",
      qualityTitle: "Сапа, сертификаттар және стандарттар",
      qualityText:
        "Тек мақұлданған бастапқы құжаттармен расталған стандарттар мен сертификаттар көрсетіледі.",
      documentsTitle: "Техникалық құжаттар орталығы",
      documentsText:
        "Қолжетімді техникалық деректер парақтарын, нұсқаулықтарды, кітапшаларды және сертификаттарды өнім мен тіл бойынша іздеңіз.",
      aboutTitle: "Metal Asia туралы",
      aboutText:
        "METAL ASIA — темір-болат өнімдерін жеткізуші және BISALLOY® компаниясының ресми дистрибьюторы.",
      contactTitle: "Талабыңызды талқылайық",
      contactText:
        "Нақты жауап алу үшін өнім, қалыңдық, мөлшер және қолдану туралы қажетті мәліметтерді жіберіңіз.",
    },
    about: {
      title: "Metal Asia туралы",
      intro:
        "METAL ASIA темір-болат өнімдерін сатумен және жеткізумен айналысады. Компания BISALLOY® компаниясының ресми дистрибьюторы болып табылады.",
      positioningTitle: "Техникалық айқындыққа негізделген",
      positioningText:
        "Өнімге қатысты тұжырымдар бастапқы құжаттармен байланысады, ал белгісіз мәндер болжанбай, ашық түрде көрсетілмеген болып қалады.",
      principlesTitle: "Ақпаратпен жұмыс істеу қағидаларымыз",
      accuracyTitle: "Дәлдік",
      accuracyText:
        "Техникалық мәндер тек мақұлданған дереккөзге дейін қадағаланғанда жарияланады.",
      traceabilityTitle: "Қадағаланымдылық",
      traceabilityText:
        "Өнім жазбаларында растайтын құжат пен соңғы тексеру күйі көрсетіледі.",
      supportTitle: "Мақсатты қолдау",
      supportText:
        "Сұраулар техникалық немесе коммерциялық өтінімді дұрыс бағыттауға қажетті қолдану мәліметтерін жинайды.",
      roleDisclosure:
        "METAL ASIA темір-болат өнімдерін сатады және жеткізеді, сондай-ақ BISALLOY® өнімдерін ресми дистрибьютор ретінде ұсынады.",
    },
    products: {
      title: "Барлық өнімдер",
      intro:
        "BISALLOY® және BISPLATE каталогынан тозуға төзімді болат, тозуға төзімді табақ, сауыттық болат, сауытты табақ, футеровка және wear liners шешімдерін іздеңіз, сүзгілеңіз және салыстырыңыз.",
      searchLabel: "Өнімдерді іздеу",
      searchPlaceholder: "Өнім, топ немесе қолдану саласы бойынша іздеу",
      resultCount: "{count} өнім",
      familyFilter: "Өнім тобы",
      applicationFilter: "Қолдану саласы",
      hardnessFilter: "Бринелль бойынша қаттылық",
      thicknessFilter: "Қалыңдық",
      yieldStrengthFilter: "Аққыштық шегі",
      tensileStrengthFilter: "Созылу беріктігі",
      viewLabel: "Нәтиже көрінісі",
      gridView: "Карточкалық көрініс",
      listView: "Тізімдік көрініс",
      activeFilters: "Белсенді сүзгілер",
      clearFilters: "Барлық сүзгіні тазалау",
      compare: "Салыстыру",
      addToCompare: "Салыстыруға қосу",
      removeFromCompare: "Салыстырудан шығару",
      compareTitle: "Өнімдерді салыстыру",
      compareHint: "Екіден төртке дейін өнім таңдаңыз.",
      compareMinimum: "Салыстыру үшін кемінде екі өнім таңдаңыз.",
      compareMaximum: "Төрт өнімге дейін салыстыруға болады.",
      compareNow: "Таңдалған өнімдерді салыстыру",
      emptyTitle: "Сәйкес өнім табылмады",
      emptyText: "Іздеуді өзгертіңіз немесе бір не бірнеше сүзгіні тазалаңыз.",
      missingNumericData:
        "Тексерілген мәні жоқ өнімдер бұл сандық сүзгіге кірмейді; жетіспейтін дерек нөл деп есептелмейді.",
      viewDetails: "Толық мәлімет",
      requestDocument: "Техникалық құжат сұрау",
      downloadDatasheet: "Техникалық деректер парағын жүктеу",
    },
    product: {
      overview: "Шолу",
      gallery: "Өнім суреттері",
      advantages: "Негізгі артықшылықтар",
      applications: "Қолдану салалары",
      technicalSpecifications: "Техникалық сипаттамалар",
      thicknessRange: "Қалыңдық ауқымы",
      brinellHardness: "Бринелль бойынша қаттылық",
      yieldStrength: "Аққыштық шегі",
      proofStrength: "Шартты аққыштық шегі",
      tensileStrength: "Созылу беріктігі",
      chemicalComposition: "Химиялық құрам",
      mechanicalProperties: "Механикалық қасиеттер",
      equivalentStandards: "Баламалы стандарттар",
      certifications: "Тексерілген сертификаттар",
      processingNotes: "Кесу, дәнекерлеу және ию ескертпелері",
      cutting: "Кесу",
      welding: "Дәнекерлеу",
      bending: "Ию",
      relatedProducts: "Қатысты өнімдер",
      documentModule: "Техникалық деректер парақтары мен қосымша құжаттар",
      dataSource: "Бастапқы құжат",
      lastVerified: "Соңғы тексеру",
      missingValue: "Тексерілген мән қолжетімсіз",
      missingDataNotice:
        "Тек ресми құжаттармен расталған өрістер көрсетіледі. Көрсетілмеген мәндер болжанбаған.",
      noDocumentTitle: "Техникалық құжат қолжетімсіз",
      noDocumentText:
        "Бұл өнімге әзірге мақұлданған құжат байланыстырылмаған. Оны техникалық топтан сұрауға болады.",
      requestDocument: "Осы құжатты сұрау",
      documentAvailableIn: "Бұл құжат {language} тілінде қолжетімді.",
      selectedLanguageUnavailable:
        "Таңдалған тілде техникалық құжат жоқ. Қолжетімді құжаттар өз тілі көрсетіліп беріледі.",
    },
    industries: {
      title: "Салалар",
      intro:
        "Арнайы болат туралы ақпаратты қолдану ортасына қарай қараңыз. Өнімнің жарамдылығы тексерілген техникалық талаптар бойынша расталуы тиіс.",
      exploreSector: "Осы саланы қарау",
      sectors: {
        defense: {
          title: "Қорғаныс және қауіпсіздік",
          description:
            "Баллистикалық және қорғаныш инженерлік бағалау үшін құжатталған сауыттық және қорғаныш болат маркаларын қараңыз.",
        },
        mining: {
          title: "Тау-кен ісі және жер қазу",
          description:
            "Тозуы жоғары өндіру және жер қазу жұмыстарына арналған тозуға төзімді әрі беріктігі жоғары маркаларды қараңыз.",
        },
        mineralProcessing: {
          title: "Минералдарды өңдеу",
          description:
            "Абразивті материал ағынына ұшырайтын науалар, қаптамалар, бункерлер және басқа бөлшектер үшін құжатталған нұсқаларды табыңыз.",
        },
        construction: {
          title: "Құрылыс",
          description:
            "Күрделі құрылымдар мен алаң жабдығына арналған беріктігі жоғары және тозуға төзімді маркаларды салыстырыңыз.",
        },
        transportation: {
          title: "Көлік",
          description:
            "Салмаққа сезімтал, жүк көтеретін және тозуға ұшырайтын көлік қолданбаларына арналған болат маркаларын бағалаңыз.",
        },
        energy: {
          title: "Энергетика",
          description:
            "Күрделі пайдалану жағдайындағы жабдық пен инфрақұрылымға арналған тексерілген болат ақпаратын қараңыз.",
        },
        agriculture: {
          title: "Ауыл шаруашылығы",
          description:
            "Топырақпен жанасатын және материал тасымалдайтын ауыл шаруашылығы бөлшектеріне арналған тозуға төзімді маркаларды қараңыз.",
        },
        engineering: {
          title: "Жалпы машина жасау және дайындау",
          description:
            "Инженерлік бөлшектер, машиналар және дайындауды жоспарлау үшін құжатталған қасиеттерді салыстырыңыз.",
        },
      },
    },
    documents: {
      title: "Техникалық құжаттар орталығы",
      intro:
        "Мақұлданған техникалық деректер парақтарын, нұсқаулықтарды, кітапшаларды және сертификаттарды өнім, топ, түр, тіл және жыл бойынша іздеңіз.",
      searchLabel: "Құжаттарды іздеу",
      searchPlaceholder: "Құжат немесе өнім атауы бойынша іздеу",
      familyFilter: "Өнім тобы",
      productFilter: "Өнім",
      typeFilter: "Құжат түрі",
      languageFilter: "Құжат тілі",
      yearFilter: "Жарияланған жылы",
      datasheet: "Техникалық деректер парағы",
      technicalGuide: "Техникалық нұсқаулық",
      brochure: "Кітапша",
      certificate: "Сертификат",
      documentLanguage: "Құжат тілі",
      version: "Нұсқа",
      publicationDate: "Жарияланған күні",
      fileSize: "Файл көлемі",
      lastUpdated: "Соңғы жаңарту",
      source: "Дереккөз",
      familyDatasheet: "Өнім тобының техникалық деректер парағы",
      selectedLanguageMissing:
        "Таңдалған интерфейс тілінде құжат жоқ; қолжетімді құжаттар нақты тілімен көрсетіледі.",
      preview: "Құжатты алдын ала қарау",
      previewUnavailable:
        "Бұл құрылғыда алдын ала қарау қолдау таппайды. Құжатты жаңа қойындыда ашыңыз немесе жүктеп алыңыз.",
      emptyTitle: "Сәйкес құжат табылмады",
      emptyText: "Іздеуді өзгертіңіз немесе бір не бірнеше құжат сүзгісін тазалаңыз.",
    },
    quality: {
      title: "Сапа, сертификаттар және стандарттар",
      intro:
        "Тек мақұлданған құжаттармен расталған сапа жазбаларын, стандарттарды және сертификаттарды қараңыз.",
      verifiedOnlyTitle: "Тек тексерілген ақпарат",
      verifiedOnlyText:
        "Стандарт немесе сертификат оның дереккөзі мен өнімге қатысы расталғаннан кейін ғана көрсетіледі.",
      certificatesTitle: "Сертификаттар",
      noCertificatesTitle: "Мақұлданған сертификаттар жарияланбаған",
      noCertificatesText:
        "Тиісті файлдар мен олардың қолданылу аясы тексерілгеннен кейін сертификаттар осында көрсетіледі.",
      standardsTitle: "Стандарттар",
      traceabilityTitle: "Құжат қадағаланымдылығы",
      traceabilityText:
        "Жарияланған жазбаларда белгілі болса, дереккөз, тіл, нұсқа, жарияланған күн және тексеру күйі сақталады.",
    },
    services: {
      title: "Техникалық қолдау және қызметтер",
      intro:
        "Өнім таңдау, техникалық құжаттар немесе қолдануға қатысты ақпарат үшін құрылымдалған сұрау жіберіңіз.",
      selectionTitle: "Өнім таңдауды қолдау",
      selectionText:
        "Құжатталған қасиеттерді салыстырып, әрі қарай бағалау үшін қолдану талаптарыңызды жіберіңіз.",
      documentsTitle: "Құжаттық қолдау",
      documentsText:
        "Қолжетімсіз техникалық деректер парағын сұраңыз немесе бар құжаттың тілі мен нұсқасын нақтылаңыз.",
      processingTitle: "Өңдеу ақпараты",
      processingText:
        "Кесу, дәнекерлеу және ию бойынша нұсқаулар тек ресми өнім құжаттарымен расталғанда беріледі.",
      enquiryTitle: "Техникалық сұрау",
      enquiryText:
        "Сұрауды дәл бағалау үшін марка, қалыңдық, мөлшер және жұмыс жағдайларын көрсетіңіз.",
    },
    contact: {
      title: "Байланыс және баға ұсынысын сұрау",
      intro:
        "Бағалап отырған өніміңіз бен техникалық талабыңызды хабарлаңыз.",
      detailsTitle: "Байланыс деректері",
      detailsUnavailable:
        "Тексерілген электрондық пошта, телефон, WhatsApp және мекенжай мәліметтері әзірге жариялауға бапталмаған.",
      quoteTitle: "Баға ұсынысын сұрау",
      quoteText:
        "Нақты жауап алу үшін өнім, қалыңдық, мөлшер және қолдану ақпаратын беріңіз.",
    },
    form: {
      fullName: "Аты-жөні",
      company: "Компания",
      email: "Электрондық пошта",
      phone: "Телефон",
      country: "Ел",
      city: "Қала",
      address: "Мекенжай",
      product: "Қызықтыратын өнім",
      thickness: "Қажетті қалыңдық",
      quantity: "Мөлшер",
      message: "Хабарлама",
      privacyConsent: "Сұрауымның өңделуі туралы құпиялылық хабарламасын оқыдым.",
      privacyLinkText: "Құпиялық саясатын оқу",
      selectPlaceholder: "Нұсқаны таңдаңыз",
      optional: "Міндетті емес",
      submit: "Сұрауды жіберу",
      submitting: "Сұрау жіберілуде",
      required: "Бұл өріс міндетті.",
      invalidEmail: "Жарамды электрондық пошта мекенжайын енгізіңіз.",
      invalidPhone: "Жарамды телефон нөмірін енгізіңіз.",
      errorSummaryTitle: "Келесі өрістерді түзетіңіз",
      spamDetected: "Сұрауды тексеру мүмкін болмады. Қайталап көріңіз.",
      successTitle: "Сұрауыңыз қабылданды",
      successText:
        "Бапталған қызмет сұрауыңыздың қабылданғанын растады. Кейін хабарласу үшін көрсетілген нөмірді сақтаңыз.",
      errorTitle: "Сұрауыңыз жіберілмеді",
      errorText:
        "Қызмет сұрауды қабылдай алмады. Өрістерді тексеріп, кейінірек қайталап көріңіз.",
      integrationMissingTitle: "Электрондық пошта арқылы жіберу қолданылады",
      integrationMissingText:
        "Сервер арқылы жіберу әзірге қосылмаған. Тексеруден кейін сұрау электрондық пошта қолданбаңызда дайын хабарлама ретінде ашылады және оны сол жерде растағаннан кейін ғана жіберіледі.",
    },
    legal: {
      privacyTitle: "Құпиялық саясаты",
      privacyBlockerTitle: "Мақұлданған құпиялық мәтіні қажет",
      privacyBlockerText:
        "Заңды тұлға, деректер операторы, өңдеу мақсаттары, сақтау мерзімдері және байланыс деректері мақұлданбайынша құпиялық саясаты жарияланбайды.",
      cookiesTitle: "Cookie файлдары саясаты",
      cookiesBlockerTitle: "Cookie файлдары туралы мақұлданған мәтін қажет",
      cookiesBlockerText:
        "Қолданылатын файлдар, олардың мақсаттары, мерзімдері, жеткізушілері және келісім алу тәртібі мақұлданбайынша саясат жарияланбайды.",
      preferenceStorageText:
        "Міндетті тіл таңдауын сақтау талдамалық және маркетингтік келісімнен бөлек жүргізіледі.",
      legalReviewRequired: "Жариялау алдында заңдық тексеру қажет.",
    },
    breadcrumbs: {
      label: "Навигациялық жол",
      home: "Басты бет",
      currentPage: "Ағымдағы бет",
    },
    notFound: {
      title: "Бет табылмады",
      text:
        "Мекенжай өзгерген болуы немесе бұл бет таңдалған тілде қолжетімсіз болуы мүмкін.",
      backHome: "Басты бетке оралу",
      browseProducts: "Барлық өнімді қарау",
    },
    footer: {
      tagline:
        "Арнайы болатты бағалауға арналған тексерілген өнім ақпараты мен техникалық құжаттар.",
      productLinksTitle: "Өнімдер",
      companyLinksTitle: "Компания",
      resourceLinksTitle: "Ресурстар",
      sitemapLabel: "XML сайт картасы",
      contactTitle: "Байланыс",
      rights: "Барлық құқықтар қорғалған.",
      legalEntityMissing: "Тексерілген заңды тұлға деректері жариялануды күтуде.",
      contactDetailsMissing: "Тексерілген байланыс деректері жариялануды күтуде.",
    },
    seo: {
      home: {
        title: "Тозуға төзімді болат және сауытты табақ | METAL ASIA",
        description:
          "BISALLOY® және BISPLATE: болат табақ, металл қаңылтыр, тозуға төзімді болат, сауытты табақ, футеровка және wear liners.",
      },
      about: {
        title: "Metal Asia туралы",
        description:
          "METAL ASIA — темір-болат өнімдерін сатушы әрі жеткізуші және BISALLOY® компаниясының ресми дистрибьюторы.",
      },
      products: {
        title: "Тозуға төзімді болат пен сауытты табақ | METAL ASIA",
        description:
          "BISALLOY® және BISPLATE каталогы: тозуға төзімді табақ, сауыттық болат, футеровка және wear liners жабдық тозуын азайтуға арналған.",
      },
      industries: {
        title: "Салалар және қолдану бағыттары | Metal Asia",
        description:
          "Құжатталған арнайы болат нұсқаларын сала, жұмыс ортасы және инженерлік талап бойынша қараңыз.",
      },
      documents: {
        title: "Техникалық құжаттар | Metal Asia",
        description:
          "Қолжетімді болат деректер парақтарын, техникалық нұсқаулықтарды, кітапшаларды және сертификаттарды өнім мен тіл бойынша іздеңіз.",
      },
      quality: {
        title: "Сапа, сертификаттар және стандарттар | Metal Asia",
        description:
          "Мақұлданған бастапқы құжаттарға байланыстырылған тексерілген сапа жазбаларын, стандарттар мен сертификаттарды қараңыз.",
      },
      services: {
        title: "Техникалық қолдау және қызметтер | Metal Asia",
        description:
          "Өнім таңдауға қолдау, техникалық құжаттар және қолдануға қатысты болат ақпаратын сұраңыз.",
      },
      contact: {
        title: "Байланыс және баға ұсынысын сұрау | Metal Asia",
        description:
          "Metal Asia компаниясына өнім, қалыңдық, мөлшер және техникалық талаптарыңызды жіберіңіз.",
      },
      privacy: {
        title: "Құпиялық саясаты | Metal Asia",
        description:
          "Metal Asia компаниясының мақұлданған құпиялық және деректерді өңдеу ақпаратын оқыңыз.",
      },
      cookies: {
        title: "Cookie файлдары саясаты | Metal Asia",
        description:
          "Metal Asia міндетті және келісімге негізделген браузерлік сақтауды қалай қолданатынын оқыңыз.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "{product} үшін тексерілген қасиеттерді, қолдану салаларын және қолжетімді техникалық құжаттарды қараңыз.",
      },
      industryDetail: {
        titleTemplate: "{industry} саласына арналған болат | Metal Asia",
        descriptionTemplate:
          "{industry} саласына арналған құжатталған арнайы болат өнімдері мен техникалық ресурстарды қараңыз.",
      },
    },
    accessibility: {
      skipToContent: "Негізгі мазмұнға өту",
      mainNavigation: "Негізгі навигация",
      languageSelector: "Тіл таңдау",
      selectLanguage: "Тілді таңдаңыз",
      openMenu: "Мәзірді ашу",
      closeMenu: "Мәзірді жабу",
      productSearch: "Өнімдер каталогынан іздеу",
      filters: "Өнім сүзгілері",
      removeFilter: "{filter} сүзгісін жою",
      gridView: "Өнімдерді карточка түрінде көрсету",
      listView: "Өнімдерді тізім түрінде көрсету",
      compareSelection: "Салыстыру үшін {count} өнім таңдалды",
      documentPreview: "Техникалық құжатты алдын ала қарау",
      previousImage: "Алдыңғы суретті көрсету",
      nextImage: "Келесі суретті көрсету",
      closeDialog: "Сұхбат терезесін жабу",
      externalLink: "Сыртқы сілтеме",
      opensNewTab: "Жаңа қойындыда ашылады",
      requiredField: "Міндетті өріс",
      errorSummary: "Пішін қателерінің жиынтығы",
      loading: "Мазмұн жүктелуде",
    },
  },
  ru: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Смотреть продукцию",
      requestQuote: "Запросить предложение",
      requestTechnicalInfo: "Запросить техническую информацию",
      learnMore: "Подробнее",
      viewAll: "Смотреть все",
      download: "Скачать",
      openInNewTab: "Открыть в новой вкладке",
      search: "Поиск",
      clear: "Очистить",
      close: "Закрыть",
      back: "Назад",
      next: "Далее",
      previous: "Назад",
      loading: "Загрузка",
      unknown: "Неизвестно",
      notAvailable: "Недоступно",
      language: "Язык",
      menu: "Меню",
    },
    nav: {
      home: "Главная",
      about: "О компании",
      products: "Продукция",
      industries: "Отрасли",
      documents: "Технические документы",
      quality: "Качество и стандарты",
      services: "Техническая поддержка",
      contact: "Контакты",
      wear: "Сравнение износа",
    },
    home: {
      eyebrow: "Металлический лист, стальной лист и высокоэффективные стали",
      title: "Технические решения из стали для сложных условий эксплуатации",
      intro:
        "Изучите BISALLOY® и BISPLATE: износостойкую сталь, износостойкий лист, броню, бронеплиту, футеровку и wear liners по проверенным техническим данным.",
      primaryCta: "Смотреть продукцию",
      secondaryCta: "Запросить предложение",
      familiesTitle: "Семейства продукции",
      familiesIntro:
        "Подбирайте продукцию по семейству стали, области применения и доступным проверенным характеристикам.",
      featuredTitle: "Избранная продукция",
      featuredIntro:
        "Ознакомьтесь с выбранными марками и техническими данными, связанными с первоисточниками.",
      industriesTitle: "Отрасли и области применения",
      industriesIntro:
        "Подбирайте сталь по условиям эксплуатации и инженерным требованиям.",
      supportTitle: "Техническая поддержка и услуги",
      supportText:
        "Запросите помощь в выборе продукции, получении документов и решении прикладных вопросов.",
      qualityTitle: "Качество, сертификаты и стандарты",
      qualityText:
        "Отображаются только стандарты и сертификаты, подтверждённые утверждёнными первичными документами.",
      documentsTitle: "Центр технической документации",
      documentsText:
        "Ищите доступные технические паспорта, руководства, брошюры и сертификаты по продукции и языку.",
      aboutTitle: "О Metal Asia",
      aboutText:
        "METAL ASIA поставляет стальную продукцию и является официальным дистрибьютором BISALLOY®.",
      contactTitle: "Обсудите вашу задачу",
      contactText:
        "Укажите продукцию, толщину, количество и условия применения, необходимые для квалифицированного ответа.",
    },
    about: {
      title: "О Metal Asia",
      intro:
        "METAL ASIA занимается продажей и поставкой стальной продукции. Компания также является официальным дистрибьютором BISALLOY®.",
      positioningTitle: "Основа — техническая ясность",
      positioningText:
        "Заявленные свойства продукции связаны с первоисточниками, а неизвестные значения не оцениваются и явно отмечаются как отсутствующие.",
      principlesTitle: "Наши принципы работы с информацией",
      accuracyTitle: "Точность",
      accuracyText:
        "Технические значения публикуются только при наличии прослеживаемого утверждённого источника.",
      traceabilityTitle: "Прослеживаемость",
      traceabilityText:
        "В карточке продукции указываются подтверждающий документ и статус последней проверки.",
      supportTitle: "Предметная поддержка",
      supportText:
        "Форма запроса собирает сведения о применении, необходимые для направления технического или коммерческого обращения.",
      roleDisclosure:
        "METAL ASIA продаёт и поставляет стальную продукцию, а также предлагает продукцию BISALLOY® в качестве официального дистрибьютора.",
    },
    products: {
      title: "Вся продукция",
      intro:
        "Ищите и сравнивайте BISALLOY® и BISPLATE: износостойкую сталь, износостойкий лист, бронеплиту, футеровку и wear liners для защиты оборудования от износа.",
      searchLabel: "Поиск продукции",
      searchPlaceholder: "Поиск по продукции, семейству или применению",
      resultCount: "Найдено: {count}",
      familyFilter: "Семейство продукции",
      applicationFilter: "Область применения",
      hardnessFilter: "Твёрдость по Бринеллю",
      thicknessFilter: "Толщина",
      yieldStrengthFilter: "Предел текучести",
      tensileStrengthFilter: "Предел прочности при растяжении",
      viewLabel: "Вид результатов",
      gridView: "Карточки",
      listView: "Список",
      activeFilters: "Активные фильтры",
      clearFilters: "Очистить все фильтры",
      compare: "Сравнить",
      addToCompare: "Добавить к сравнению",
      removeFromCompare: "Убрать из сравнения",
      compareTitle: "Сравнение продукции",
      compareHint: "Выберите от двух до четырёх позиций.",
      compareMinimum: "Для сравнения выберите не менее двух позиций.",
      compareMaximum: "Можно сравнить не более четырёх позиций.",
      compareNow: "Сравнить выбранное",
      emptyTitle: "Подходящая продукция не найдена",
      emptyText: "Измените поисковый запрос или очистите один или несколько фильтров.",
      missingNumericData:
        "Продукция без проверенного значения исключается из этого числового фильтра; отсутствующие данные не считаются нулём.",
      viewDetails: "Подробнее",
      requestDocument: "Запросить технический документ",
      downloadDatasheet: "Скачать технический паспорт",
    },
    product: {
      overview: "Обзор",
      gallery: "Галерея продукции",
      advantages: "Ключевые преимущества",
      applications: "Области применения",
      technicalSpecifications: "Технические характеристики",
      thicknessRange: "Диапазон толщины",
      brinellHardness: "Твёрдость по Бринеллю",
      yieldStrength: "Предел текучести",
      proofStrength: "Условный предел текучести",
      tensileStrength: "Предел прочности при растяжении",
      chemicalComposition: "Химический состав",
      mechanicalProperties: "Механические свойства",
      equivalentStandards: "Эквивалентные стандарты",
      certifications: "Подтверждённые сертификаты",
      processingNotes: "Указания по резке, сварке и гибке",
      cutting: "Резка",
      welding: "Сварка",
      bending: "Гибка",
      relatedProducts: "Сопутствующая продукция",
      documentModule: "Технические паспорта и сопроводительные документы",
      dataSource: "Документ-источник",
      lastVerified: "Последняя проверка",
      missingValue: "Проверенное значение отсутствует",
      missingDataNotice:
        "Показаны только поля, подтверждённые официальными документами. Неуказанные значения не рассчитывались.",
      noDocumentTitle: "Технический документ отсутствует",
      noDocumentText:
        "К этой продукции пока не привязан утверждённый документ. Его можно запросить у технической службы.",
      requestDocument: "Запросить этот документ",
      documentAvailableIn: "Этот документ доступен на языке: {language}.",
      selectedLanguageUnavailable:
        "Технический документ на выбранном языке отсутствует. Доступные документы по-прежнему показаны с указанием языка.",
    },
    industries: {
      title: "Отрасли",
      intro:
        "Изучайте специализированные стали по условиям применения. Пригодность продукции должна подтверждаться по проверенным техническим требованиям.",
      exploreSector: "Перейти к отрасли",
      sectors: {
        defense: {
          title: "Оборона и безопасность",
          description:
            "Ознакомьтесь с документированными марками броневой и защитной стали для оценки баллистических и защитных конструкций.",
        },
        mining: {
          title: "Горная добыча и земляные работы",
          description:
            "Изучите износостойкие и высокопрочные марки для добычи и землеройных работ с интенсивным абразивным износом.",
        },
        mineralProcessing: {
          title: "Переработка минерального сырья",
          description:
            "Предлагаем документированные варианты для желобов, футеровок, бункеров и других деталей в потоках абразивного материала.",
        },
        construction: {
          title: "Строительство",
          description:
            "Сравните высокопрочные и износостойкие марки для ответственных конструкций и строительной техники.",
        },
        transportation: {
          title: "Транспорт",
          description:
            "Оцените марки стали для транспортных конструкций, где важны масса, несущая способность и стойкость к износу.",
        },
        energy: {
          title: "Энергетика",
          description:
            "Изучите проверенные данные о стали для оборудования и инфраструктуры в сложных условиях эксплуатации.",
        },
        agriculture: {
          title: "Сельское хозяйство",
          description:
            "Рассмотрите износостойкие марки для почвообрабатывающих и транспортирующих деталей сельскохозяйственной техники.",
        },
        engineering: {
          title: "Общее машиностроение и изготовление",
          description:
            "Сравните документированные свойства для проектируемых деталей, машин и планирования изготовления.",
        },
      },
    },
    documents: {
      title: "Центр технической документации",
      intro:
        "Ищите утверждённые технические паспорта, руководства, брошюры и сертификаты по продукции, семейству, типу, языку и году.",
      searchLabel: "Поиск документов",
      searchPlaceholder: "Поиск по названию документа или продукции",
      familyFilter: "Семейство продукции",
      productFilter: "Продукция",
      typeFilter: "Тип документа",
      languageFilter: "Язык документа",
      yearFilter: "Год публикации",
      datasheet: "Технический паспорт",
      technicalGuide: "Техническое руководство",
      brochure: "Брошюра",
      certificate: "Сертификат",
      documentLanguage: "Язык документа",
      version: "Версия",
      publicationDate: "Дата публикации",
      fileSize: "Размер файла",
      lastUpdated: "Последнее обновление",
      source: "Источник",
      familyDatasheet: "Технический паспорт семейства продукции",
      selectedLanguageMissing:
        "Документ на выбранном языке интерфейса отсутствует; доступные документы показаны с указанием их фактического языка.",
      preview: "Предпросмотр документа",
      previewUnavailable:
        "Предпросмотр не поддерживается на этом устройстве. Откройте документ в новой вкладке или скачайте его.",
      emptyTitle: "Подходящие документы не найдены",
      emptyText: "Измените поисковый запрос или очистите один или несколько фильтров.",
    },
    quality: {
      title: "Качество, сертификаты и стандарты",
      intro:
        "Просматривайте только те сведения о качестве, стандарты и сертификаты, которые подтверждены утверждённой документацией.",
      verifiedOnlyTitle: "Только проверенная информация",
      verifiedOnlyText:
        "Стандарт или сертификат отображается только после проверки его источника и связи с продукцией.",
      certificatesTitle: "Сертификаты",
      noCertificatesTitle: "Утверждённые сертификаты не опубликованы",
      noCertificatesText:
        "Сертификаты появятся здесь после проверки соответствующих файлов и области их действия.",
      standardsTitle: "Стандарты",
      traceabilityTitle: "Прослеживаемость документов",
      traceabilityText:
        "Для опубликованных записей сохраняются известные сведения об источнике, языке, версии, дате публикации и статусе проверки.",
    },
    services: {
      title: "Техническая поддержка и услуги",
      intro:
        "Отправьте структурированный запрос по выбору продукции, техническим документам или информации для конкретного применения.",
      selectionTitle: "Поддержка при выборе продукции",
      selectionText:
        "Сравните документированные свойства и сообщите требования вашего применения для дальнейшего рассмотрения.",
      documentsTitle: "Поддержка по документации",
      documentsText:
        "Запросите отсутствующий технический паспорт или уточните язык и редакцию существующего документа.",
      processingTitle: "Информация по обработке",
      processingText:
        "Рекомендации по резке, сварке и гибке приводятся только при наличии официальной документации на продукцию.",
      enquiryTitle: "Технический запрос",
      enquiryText:
        "Укажите марку, толщину, количество и условия эксплуатации для точной оценки запроса.",
    },
    contact: {
      title: "Контакты и запрос предложения",
      intro:
        "Сообщите, какую продукцию и техническое требование вы оцениваете.",
      detailsTitle: "Контактные данные",
      detailsUnavailable:
        "Проверенные адрес электронной почты, телефон, WhatsApp и почтовый адрес пока не настроены для публикации.",
      quoteTitle: "Запросить предложение",
      quoteText:
        "Для квалифицированного ответа укажите продукцию, толщину, количество и условия применения.",
    },
    form: {
      fullName: "Имя и фамилия",
      company: "Компания",
      email: "Электронная почта",
      phone: "Телефон",
      country: "Страна",
      city: "Город",
      address: "Адрес",
      product: "Интересующая продукция",
      thickness: "Требуемая толщина",
      quantity: "Количество",
      message: "Сообщение",
      privacyConsent: "Я ознакомился(-лась) с уведомлением о конфиденциальности и обработке моего запроса.",
      privacyLinkText: "Прочитать политику конфиденциальности",
      selectPlaceholder: "Выберите вариант",
      optional: "Необязательно",
      submit: "Отправить запрос",
      submitting: "Запрос отправляется",
      required: "Поле обязательно для заполнения.",
      invalidEmail: "Введите действительный адрес электронной почты.",
      invalidPhone: "Введите действительный номер телефона.",
      errorSummaryTitle: "Исправьте следующие поля",
      spamDetected: "Не удалось проверить запрос. Повторите попытку.",
      successTitle: "Ваш запрос получен",
      successText:
        "Подключённая служба подтвердила получение запроса. Сохраните указанный номер для последующего обращения.",
      errorTitle: "Запрос не отправлен",
      errorText:
        "Служба не смогла принять запрос. Проверьте поля и повторите попытку позже.",
      integrationMissingTitle: "Будет использована отправка по электронной почте",
      integrationMissingText:
        "Отправка через сервер пока не подключена. После проверки запрос откроется как подготовленное сообщение в вашей почтовой программе и будет отправлен только после вашего подтверждения.",
    },
    legal: {
      privacyTitle: "Политика конфиденциальности",
      privacyBlockerTitle: "Требуется утверждённый текст о конфиденциальности",
      privacyBlockerText:
        "Политику нельзя публиковать до утверждения юридического лица, оператора данных, целей обработки, сроков хранения и контактных данных.",
      cookiesTitle: "Политика файлов cookie",
      cookiesBlockerTitle: "Требуется утверждённый текст о файлах cookie",
      cookiesBlockerText:
        "Политику нельзя публиковать до утверждения перечня используемых файлов, их целей, сроков, поставщиков и порядка получения согласия.",
      preferenceStorageText:
        "Обязательное сохранение языковых настроек отделено от согласия на аналитику и маркетинг.",
      legalReviewRequired: "Перед публикацией требуется юридическая проверка.",
    },
    breadcrumbs: {
      label: "Навигационная цепочка",
      home: "Главная",
      currentPage: "Текущая страница",
    },
    notFound: {
      title: "Страница не найдена",
      text:
        "Адрес мог измениться, либо эта страница недоступна на выбранном языке.",
      backHome: "На главную",
      browseProducts: "Смотреть всю продукцию",
    },
    footer: {
      tagline:
        "Проверенные сведения о продукции и технические документы для оценки специализированных сталей.",
      productLinksTitle: "Продукция",
      companyLinksTitle: "Компания",
      resourceLinksTitle: "Материалы",
      sitemapLabel: "XML-карта сайта",
      contactTitle: "Контакты",
      rights: "Все права защищены.",
      legalEntityMissing: "Проверенные сведения о юридическом лице ожидают публикации.",
      contactDetailsMissing: "Проверенные контактные данные ожидают публикации.",
    },
    seo: {
      home: {
        title: "Листовая сталь, износостойкая сталь и бронеплита | METAL ASIA",
        description:
          "BISALLOY® и BISPLATE: листовой металл, износостойкая сталь, бронеплита, футеровка и wear liners для промышленности.",
      },
      about: {
        title: "О Metal Asia",
        description:
          "METAL ASIA — продавец и поставщик стальной продукции, а также официальный дистрибьютор BISALLOY®.",
      },
      products: {
        title: "Износостойкая сталь, бронеплита и стальной лист | METAL ASIA",
        description:
          "Каталог BISALLOY® и BISPLATE: износостойкий лист, броневая сталь, бронеплита, футеровка и wear liners для защиты от износа.",
      },
      industries: {
        title: "Отрасли и применения | Metal Asia",
        description:
          "Изучите документированные варианты специализированных сталей по отрасли, условиям эксплуатации и инженерным требованиям.",
      },
      documents: {
        title: "Технические документы | Metal Asia",
        description:
          "Ищите доступные технические паспорта, руководства, брошюры и сертификаты по продукции и языку.",
      },
      quality: {
        title: "Качество, сертификаты и стандарты | Metal Asia",
        description:
          "Просмотрите проверенные сведения о качестве, стандарты и сертификаты, связанные с утверждёнными первичными документами.",
      },
      services: {
        title: "Техническая поддержка и услуги | Metal Asia",
        description:
          "Запросите помощь в выборе продукции, технические документы и сведения о стали для конкретного применения.",
      },
      contact: {
        title: "Контакты и запрос предложения | Metal Asia",
        description:
          "Отправьте Metal Asia сведения о продукции, толщине, количестве и технических требованиях.",
      },
      privacy: {
        title: "Политика конфиденциальности | Metal Asia",
        description:
          "Прочитайте утверждённую информацию Metal Asia о конфиденциальности и обработке данных.",
      },
      cookies: {
        title: "Политика файлов cookie | Metal Asia",
        description:
          "Узнайте, как Metal Asia использует обязательное и основанное на согласии хранение данных в браузере.",
      },
      productDetail: {
        titleTemplate: "{product} | Metal Asia",
        descriptionTemplate:
          "Изучите проверенные свойства, области применения и доступные технические документы для {product}.",
      },
      industryDetail: {
        titleTemplate: "Стали для отрасли «{industry}» | Metal Asia",
        descriptionTemplate:
          "Изучите документированные специализированные стали и технические материалы для отрасли «{industry}».",
      },
    },
    accessibility: {
      skipToContent: "Перейти к основному содержимому",
      mainNavigation: "Основная навигация",
      languageSelector: "Выбор языка",
      selectLanguage: "Выберите язык",
      openMenu: "Открыть меню",
      closeMenu: "Закрыть меню",
      productSearch: "Поиск по каталогу продукции",
      filters: "Фильтры продукции",
      removeFilter: "Удалить фильтр {filter}",
      gridView: "Показать продукцию карточками",
      listView: "Показать продукцию списком",
      compareSelection: "Для сравнения выбрано позиций: {count}",
      documentPreview: "Предпросмотр технического документа",
      previousImage: "Показать предыдущее изображение",
      nextImage: "Показать следующее изображение",
      closeDialog: "Закрыть диалоговое окно",
      externalLink: "Внешняя ссылка",
      opensNewTab: "Откроется в новой вкладке",
      requiredField: "Обязательное поле",
      errorSummary: "Сводка ошибок формы",
      loading: "Содержимое загружается",
    },
  },
  tr: {
    common: {
      companyName: "METAL ASIA",
      exploreProducts: "Ürünleri incele",
      requestQuote: "Teklif al",
      requestTechnicalInfo: "Teknik bilgi iste",
      learnMore: "Daha fazla bilgi",
      viewAll: "Tümünü görüntüle",
      download: "İndir",
      openInNewTab: "Yeni sekmede aç",
      search: "Ara",
      clear: "Temizle",
      close: "Kapat",
      back: "Geri",
      next: "Sonraki",
      previous: "Önceki",
      loading: "Yükleniyor",
      unknown: "Bilinmiyor",
      notAvailable: "Mevcut değil",
      language: "Dil",
      menu: "Menü",
    },
    nav: {
      home: "Ana sayfa",
      about: "Hakkımızda",
      products: "Ürünler",
      industries: "Sektörler",
      documents: "Teknik dokümanlar",
      quality: "Kalite ve standartlar",
      services: "Teknik destek",
      contact: "İletişim",
      wear: "Aşınma karşılaştırması",
    },
    home: {
      eyebrow: "Metal levha, çelik sac ve yüksek performanslı çelikler",
      title: "Zorlu uygulamalar için doğrulanmış çelik bilgisi",
      intro:
        "METAL ASIA; aşınmaya dayanıklı levha, yüksek dayanımlı yapısal çelik, zırh ve koruma çeliği ürünlerini mevcut doğrulanmış teknik değerler ve açık kaynak bağlantılarıyla tek bir katalogda düzenler.",
      primaryCta: "Ürünleri incele",
      secondaryCta: "Teklif al",
      familiesTitle: "Ürün aileleri",
      familiesIntro:
        "Ürünleri çelik ailesine, kullanım alanına ve doğrulanmış mevcut özelliklerine göre bulun.",
      featuredTitle: "Öne çıkan ürünler",
      featuredIntro:
        "Seçili kaliteleri inceleyin ve kaynak belgelerine bağlı teknik bilgilere ulaşın.",
      industriesTitle: "Sektörler ve uygulamalar",
      industriesIntro:
        "Çelik seçeneklerine çalışma ortamı ve mühendislik gereksinimine göre ulaşın.",
      supportTitle: "Teknik destek ve hizmetler",
      supportText:
        "Ürün seçimi, belge erişimi ve uygulama soruları için destek talep edin.",
      qualityTitle: "Kalite, sertifikalar ve standartlar",
      qualityText:
        "Yalnızca onaylı kaynak belgelerle doğrulanan standartlar ve sertifikalar gösterilir.",
      documentsTitle: "Teknik Doküman Merkezi",
      documentsText:
        "Mevcut teknik veri föylerini, teknik kılavuzları, broşürleri ve sertifikaları ürün ve dile göre arayın.",
      aboutTitle: "METAL ASIA hakkında",
      aboutText:
        "METAL ASIA, demir-çelik sektöründe faaliyet gösteren bir çelik ürünleri satıcısı ve BISALLOY®’un resmî distribütörüdür. Yüksek performanslı BISALLOY® çelik ürünlerini teknik bilgi ve dokümanlarıyla birlikte sunar.",
      contactTitle: "İhtiyacınızı görüşelim",
      contactText:
        "Nitelikli yanıt için gereken ürün, kalınlık, miktar ve uygulama bilgilerini paylaşın.",
    },
    about: {
      title: "METAL ASIA hakkında",
      intro:
        "METAL ASIA, demir-çelik sektöründe faaliyet gösteren, BISALLOY® çelik ürünlerinin satışını gerçekleştiren ve BISALLOY®’un resmî distribütörü olan bir firmadır. Aşınmaya dayanıklı, yüksek dayanımlı ve koruma amaçlı çelik ürünlerini farklı endüstriyel uygulamalara sunar.",
      positioningTitle: "Ürün iddiasından önce kaynak",
      positioningText:
        "Her teknik değer kaynağıyla birlikte gösterilir; resmî belgeyle desteklenmeyen alanlar tahmin edilmez. Böylece aşınma, dayanım ve koruma gereksinimleri ürün aileleri arasında daha açık biçimde karşılaştırılabilir.",
      principlesTitle: "Bilgi ilkelerimiz",
      accuracyTitle: "Doğruluk",
      accuracyText:
        "Teknik değerler yalnızca onaylı bir kaynağa kadar izlenebildiğinde yayımlanır.",
      traceabilityTitle: "İzlenebilirlik",
      traceabilityText:
        "Ürün kayıtları dayanak belgelerini ve en son doğrulama durumunu belirtir.",
      supportTitle: "İhtiyaca uygun destek",
      supportText:
        "Talepler, teknik veya ticari başvurunun doğru yönlendirilmesi için gereken uygulama bilgilerini içerir.",
      roleDisclosure:
        "METAL ASIA, BISALLOY®’un resmî distribütörü ve yüksek performanslı BISALLOY® çelik ürünlerinin satıcısıdır.",
    },
    products: {
      title: "Tüm ürünler",
      intro:
        "Mevcut olmayan teknik değerleri sıfır kabul etmeden uzman çelik kalitelerini arayın, filtreleyin ve karşılaştırın.",
      searchLabel: "Ürünlerde ara",
      searchPlaceholder: "Ürün, aile veya kullanım alanı ara",
      resultCount: "{count} ürün",
      familyFilter: "Ürün ailesi",
      applicationFilter: "Kullanım alanı",
      hardnessFilter: "Brinell sertliği",
      thicknessFilter: "Kalınlık",
      yieldStrengthFilter: "Akma dayanımı",
      tensileStrengthFilter: "Çekme dayanımı",
      viewLabel: "Sonuç görünümü",
      gridView: "Kart görünümü",
      listView: "Liste görünümü",
      activeFilters: "Etkin filtreler",
      clearFilters: "Tüm filtreleri temizle",
      compare: "Karşılaştır",
      addToCompare: "Karşılaştırmaya ekle",
      removeFromCompare: "Karşılaştırmadan çıkar",
      compareTitle: "Ürün karşılaştırması",
      compareHint: "İki ile dört arasında ürün seçin.",
      compareMinimum: "Karşılaştırmak için en az iki ürün seçin.",
      compareMaximum: "En fazla dört ürünü karşılaştırabilirsiniz.",
      compareNow: "Seçili ürünleri karşılaştır",
      emptyTitle: "Eşleşen ürün bulunamadı",
      emptyText: "Aramanızı değiştirin veya bir ya da daha fazla filtreyi temizleyin.",
      missingNumericData:
        "Doğrulanmış değeri olmayan ürünler bu sayısal filtrenin dışında tutulur; eksik veri sıfır sayılmaz.",
      viewDetails: "Detayları gör",
      requestDocument: "Teknik doküman talep et",
      downloadDatasheet: "Teknik veri föyünü indir",
    },
    product: {
      overview: "Genel bakış",
      gallery: "Ürün galerisi",
      advantages: "Başlıca avantajlar",
      applications: "Kullanım alanları",
      technicalSpecifications: "Teknik özellikler",
      thicknessRange: "Kalınlık aralığı",
      brinellHardness: "Brinell sertliği",
      yieldStrength: "Akma dayanımı",
      proofStrength: "Kanıt dayanımı",
      tensileStrength: "Çekme dayanımı",
      chemicalComposition: "Kimyasal bileşim",
      mechanicalProperties: "Mekanik özellikler",
      equivalentStandards: "Eşdeğer standartlar",
      certifications: "Doğrulanmış sertifikalar",
      processingNotes: "Kesim, kaynak ve büküm notları",
      cutting: "Kesim",
      welding: "Kaynak",
      bending: "Büküm",
      relatedProducts: "İlgili ürünler",
      documentModule: "Teknik veri föyleri ve ek belgeler",
      dataSource: "Kaynak belge",
      lastVerified: "Son doğrulama",
      missingValue: "Doğrulanmış değer mevcut değil",
      missingDataNotice:
        "Yalnızca resmî belgelerle desteklenen alanlar gösterilir. Bildirilmeyen değerler tahmin edilmemiştir.",
      noDocumentTitle: "Teknik doküman mevcut değil",
      noDocumentText:
        "Bu ürüne bağlı onaylı bir belge henüz bulunmuyor. Belgeyi teknik ekipten talep edebilirsiniz.",
      requestDocument: "Bu dokümanı talep et",
      documentAvailableIn: "Bu belge {language} dilinde mevcuttur.",
      selectedLanguageUnavailable:
        "Seçilen dilde teknik belge bulunmuyor. Mevcut belgeler kendi dilleri belirtilerek listelenmeye devam eder.",
    },
    industries: {
      title: "Sektörler",
      intro:
        "METAL ASIA kataloğundaki ürün ailelerini aşınma, yük taşıma ve koruma gereksinimleri bağlamında inceleyin. Nihai ürün ve kalınlık seçimi doğrulanmış teknik gereksinimlere göre teyit edilmelidir.",
      exploreSector: "Bu sektörü incele",
      sectors: {
        defense: {
          title: "Savunma ve güvenlik",
          description:
            "Balistik ve koruyucu mühendislik değerlendirmeleri için belgelenmiş zırh ve koruma çeliği kalitelerini inceleyin.",
        },
        mining: {
          title: "Madencilik ve hafriyat",
          description:
            "Yoğun aşınmalı çıkarma ve toprak hareketi uygulamaları için aşınmaya dirençli ve yüksek dayanımlı kaliteleri inceleyin.",
        },
        mineralProcessing: {
          title: "Mineral işleme",
          description:
            "Aşındırıcı malzeme akışına maruz kalan oluk, astar, bunker ve diğer bileşenler için belgelenmiş seçenekleri bulun.",
        },
        construction: {
          title: "İnşaat",
          description:
            "Zorlu yapı ve saha ekipmanı gereksinimleri için yüksek dayanımlı ve aşınmaya dirençli kaliteleri karşılaştırın.",
        },
        transportation: {
          title: "Taşımacılık",
          description:
            "Ağırlığa duyarlı, yük taşıyan ve aşınmaya maruz kalan taşıma uygulamaları için çelik kalitelerini değerlendirin.",
        },
        energy: {
          title: "Enerji",
          description:
            "Zorlu hizmet koşullarında çalışan ekipman ve altyapı için doğrulanmış çelik bilgilerine ulaşın.",
        },
        agriculture: {
          title: "Tarım",
          description:
            "Toprakla temas eden ve malzeme taşıyan tarım bileşenleri için aşınmaya dirençli kaliteleri inceleyin.",
        },
        engineering: {
          title: "Genel mühendislik ve imalat",
          description:
            "Mühendislik bileşenleri, makineler ve imalat planlaması için belgelenmiş özellikleri karşılaştırın.",
        },
      },
    },
    documents: {
      title: "Teknik Doküman Merkezi",
      intro:
        "METAL ASIA Teknik Doküman Merkezi’nde mevcut onaylı belgeleri ürün, aile, belge türü, dil ve yayın yılına göre bulun. Belgesi bulunmayan ürünler açıkça işaretlenir.",
      searchLabel: "Dokümanlarda ara",
      searchPlaceholder: "Doküman veya ürün adı ara",
      familyFilter: "Ürün ailesi",
      productFilter: "Ürün",
      typeFilter: "Doküman türü",
      languageFilter: "Doküman dili",
      yearFilter: "Yayın yılı",
      datasheet: "Teknik veri föyü",
      technicalGuide: "Teknik kılavuz",
      brochure: "Broşür",
      certificate: "Sertifika",
      documentLanguage: "Doküman dili",
      version: "Sürüm",
      publicationDate: "Yayın tarihi",
      fileSize: "Dosya boyutu",
      lastUpdated: "Son güncelleme",
      source: "Kaynak",
      familyDatasheet: "Ürün ailesi teknik veri föyü",
      selectedLanguageMissing:
        "Seçilen arayüz dilinde belge bulunmuyor; mevcut belgeler gerçek dilleri belirtilerek gösteriliyor.",
      preview: "Dokümanı önizle",
      previewUnavailable:
        "Bu cihazda önizleme desteklenmiyor. Belgeyi yeni sekmede açın veya indirin.",
      emptyTitle: "Eşleşen doküman bulunamadı",
      emptyText: "Aramanızı değiştirin veya bir ya da daha fazla doküman filtresini temizleyin.",
    },
    quality: {
      title: "Kalite, sertifikalar ve standartlar",
      intro:
        "METAL ASIA, standart ve sertifika bilgisini yalnızca onaylı kaynak belgesi ve doğrulanmış ürün ilişkisi bulunduğunda gösterir. Belge bulunmadığında bu durum açıkça belirtilir.",
      verifiedOnlyTitle: "Yalnızca doğrulanmış bilgi",
      verifiedOnlyText:
        "Bir standart veya sertifika ancak kaynağı ve ürünle ilişkisi doğrulandığında gösterilir.",
      certificatesTitle: "Sertifikalar",
      noCertificatesTitle: "Yayımlanmış onaylı sertifika yok",
      noCertificatesText:
        "Sertifikalar, ilgili dosyalar ve kapsamları doğrulandıktan sonra burada gösterilecektir.",
      standardsTitle: "Standartlar",
      traceabilityTitle: "Belge izlenebilirliği",
      traceabilityText:
        "Yayımlanan kayıtlar, biliniyorsa kaynak, dil, sürüm, yayın tarihi ve doğrulama durumunu korur.",
    },
    services: {
      title: "Teknik destek ve hizmetler",
      intro:
        "METAL ASIA; ürün seçimi, belge erişimi ve uygulamaya özel teknik gereksinimlerin netleştirilmesi için yapılandırılmış bir talep akışı sağlar. Kesim, kaynak ve büküm bilgileri yalnızca resmî ürün belgelerine dayanır.",
      selectionTitle: "Ürün seçimi desteği",
      selectionText:
        "Belgelenmiş özellikleri karşılaştırın ve ek değerlendirme için uygulama gereksinimlerinizi paylaşın.",
      documentsTitle: "Doküman desteği",
      documentsText:
        "Mevcut olmayan teknik veri föyünü talep edin veya var olan belgenin dili ve sürümü hakkında bilgi alın.",
      processingTitle: "İşleme bilgileri",
      processingText:
        "Kesim, kaynak ve büküm bilgileri yalnızca resmî ürün belgeleriyle desteklendiğinde sunulur.",
      enquiryTitle: "Teknik bilgi talebi",
      enquiryText:
        "Talebin doğru değerlendirilmesi için kalite, kalınlık, miktar ve çalışma koşullarını belirtin.",
    },
    contact: {
      title: "İletişim ve teklif talebi",
      intro:
        "Değerlendirdiğiniz ürünü ve teknik gereksinimi paylaşın.",
      detailsTitle: "İletişim bilgileri",
      detailsUnavailable:
        "Doğrulanmış e-posta, telefon, WhatsApp ve adres bilgileri henüz yayın için yapılandırılmadı.",
      quoteTitle: "Teklif al",
      quoteText:
        "Nitelikli yanıt için ürün, kalınlık, miktar ve uygulama bilgilerini belirtin.",
    },
    form: {
      fullName: "Ad ve soyad",
      company: "Şirket",
      email: "E-posta",
      phone: "Telefon",
      country: "Ülke",
      city: "Şehir",
      address: "Adres",
      product: "İlgilenilen ürün",
      thickness: "İstenen kalınlık",
      quantity: "Miktar",
      message: "Mesaj",
      privacyConsent: "Talebimin nasıl işlendiğini açıklayan gizlilik bildirimini okudum.",
      privacyLinkText: "Gizlilik politikasını oku",
      selectPlaceholder: "Bir seçenek belirleyin",
      optional: "İsteğe bağlı",
      submit: "Talebi gönder",
      submitting: "Talep gönderiliyor",
      required: "Bu alan zorunludur.",
      invalidEmail: "Geçerli bir e-posta adresi girin.",
      invalidPhone: "Geçerli bir telefon numarası girin.",
      errorSummaryTitle: "Lütfen aşağıdaki alanları düzeltin",
      spamDetected: "Talep doğrulanamadı. Lütfen yeniden deneyin.",
      successTitle: "Talebiniz alındı",
      successText:
        "Talebiniz güvenli gönderim hizmeti tarafından alındı.",
      errorTitle: "Talebiniz gönderilmedi",
      errorText:
        "Talebiniz iletilemedi. Alanları kontrol edip daha sonra yeniden deneyin.",
      integrationMissingTitle: "E-posta ile gönderim kullanılacak",
      integrationMissingText:
        "Sunucu üzerinden gönderim henüz bağlı değil. Kontrolden sonra talep e-posta programınızda hazırlanmış bir mesaj olarak açılır ve yalnızca orada onayladığınızda gönderilir.",
    },
    legal: {
      privacyTitle: "Gizlilik politikası",
      privacyBlockerTitle: "Onaylı gizlilik metni gerekli",
      privacyBlockerText:
        "Yasal unvan, veri sorumlusu, işleme amaçları, saklama süreleri ve iletişim bilgileri onaylanmadan gizlilik politikası yayımlanamaz.",
      cookiesTitle: "Çerez politikası",
      cookiesBlockerTitle: "Onaylı çerez metni gerekli",
      cookiesBlockerText:
        "Kullanılan çerezler, amaçlar, süreler, sağlayıcılar ve onay süreci belirlenip onaylanmadan çerez politikası yayımlanamaz.",
      preferenceStorageText:
        "Zorunlu dil tercihi saklama mekanizması, analitik ve pazarlama onayından ayrı tutulur.",
      legalReviewRequired: "Yayımdan önce hukuki inceleme gereklidir.",
    },
    breadcrumbs: {
      label: "Sayfa yolu",
      home: "Ana sayfa",
      currentPage: "Geçerli sayfa",
    },
    notFound: {
      title: "Sayfa bulunamadı",
      text:
        "Adres değişmiş olabilir veya bu sayfa seçilen dilde mevcut olmayabilir.",
      backHome: "Ana sayfaya dön",
      browseProducts: "Tüm ürünleri incele",
    },
    footer: {
      tagline:
        "Aşınma levhaları ve yüksek performanslı çelikler için doğrulanmış ürün bilgisi ve teknik doküman erişimi.",
      productLinksTitle: "Ürünler",
      companyLinksTitle: "Şirket",
      resourceLinksTitle: "Kaynaklar",
      sitemapLabel: "XML site haritası",
      contactTitle: "İletişim",
      rights: "Tüm hakları saklıdır.",
      legalEntityMissing: "Doğrulanmış yasal unvan bilgileri yayınlanmayı bekliyor.",
      contactDetailsMissing: "Doğrulanmış iletişim bilgileri yayınlanmayı bekliyor.",
    },
    seo: {
      home: {
        title: "Çelik Levha ve Metal Sac | BISALLOY® | METAL ASIA",
        description:
          "METAL ASIA; BISALLOY® çelik levha, metal sac, aşınmaya dayanıklı çelik levha, yüksek dayanımlı yapısal çelik ve zırh çeliği sunar.",
      },
      about: {
        title: "METAL ASIA Hakkında",
        description:
          "Demir-çelik sektöründe faaliyet gösteren METAL ASIA’yı, BISALLOY® resmî distribütörlüğünü ve sunduğu yüksek performanslı çelik ürünlerini tanıyın.",
      },
      products: {
        title: "Çelik Levha ve Aşınmaya Dayanıklı Çelik | METAL ASIA",
        description:
          "Yüksek dayanımlı, aşınmaya dirençli, zırh ve koruma çeliği kalitelerini arayın, filtreleyin ve karşılaştırın.",
      },
      industries: {
        title: "Sektörler ve Uygulamalar | METAL ASIA",
        description:
          "Belgelenmiş uzman çelik seçeneklerini sektöre, çalışma ortamına ve mühendislik gereksinimine göre inceleyin.",
      },
      documents: {
        title: "Teknik Dokümanlar | METAL ASIA",
        description:
          "Mevcut çelik teknik veri föylerini, teknik kılavuzları, broşürleri ve sertifikaları ürün ve dile göre arayın.",
      },
      quality: {
        title: "Kalite, Sertifikalar ve Standartlar | METAL ASIA",
        description:
          "Onaylı kaynak belgelere bağlı doğrulanmış kalite kayıtlarını, standartları ve sertifikaları inceleyin.",
      },
      services: {
        title: "Teknik Destek ve Hizmetler | METAL ASIA",
        description:
          "Ürün seçimi desteği, teknik dokümanlar ve uygulamaya özel çelik bilgisi talep edin.",
      },
      contact: {
        title: "İletişim ve Teklif Talebi | METAL ASIA",
        description:
          "Ürün, kalınlık, miktar ve teknik gereksinimlerinizi METAL ASIA’ya iletin.",
      },
      privacy: {
        title: "Gizlilik Politikası | METAL ASIA",
        description:
          "METAL ASIA gizlilik sayfasının yayın durumunu ve hukuki inceleme gereksinimlerini görüntüleyin.",
      },
      cookies: {
        title: "Çerez Politikası | METAL ASIA",
        description:
          "METAL ASIA çerez sayfasının yayın durumunu ve zorunlu dil tercihi saklama bilgisini görüntüleyin.",
      },
      productDetail: {
        titleTemplate: "{product} | METAL ASIA",
        descriptionTemplate:
          "{product} için doğrulanmış özellikleri, kullanım alanlarını ve mevcut teknik dokümanları inceleyin.",
      },
      industryDetail: {
        titleTemplate: "{industry} için Çelik Uygulamaları | METAL ASIA",
        descriptionTemplate:
          "{industry} için belgelenmiş uzman çelik ürünlerini ve teknik kaynakları inceleyin.",
      },
    },
    accessibility: {
      skipToContent: "Ana içeriğe geç",
      mainNavigation: "Ana gezinme",
      languageSelector: "Dil seçici",
      selectLanguage: "Dil seç",
      openMenu: "Menüyü aç",
      closeMenu: "Menüyü kapat",
      productSearch: "Ürün kataloğunda ara",
      filters: "Ürün filtreleri",
      removeFilter: "{filter} filtresini kaldır",
      gridView: "Ürünleri kart olarak göster",
      listView: "Ürünleri liste olarak göster",
      compareSelection: "Karşılaştırma için {count} ürün seçildi",
      documentPreview: "Teknik doküman önizlemesi",
      previousImage: "Önceki görseli göster",
      nextImage: "Sonraki görseli göster",
      closeDialog: "İletişim kutusunu kapat",
      externalLink: "Harici bağlantı",
      opensNewTab: "Yeni sekmede açılır",
      requiredField: "Zorunlu alan",
      errorSummary: "Form hata özeti",
      loading: "İçerik yükleniyor",
    },
  },
} as const satisfies Record<LegacyLocale, TranslationMessages>;

export const messages = extendLocaleRecord<TranslationMessages>(
  legacyMessages,
);

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

export function getMessages(locale: Locale): TranslationMessages {
  return messages[locale];
}

export function getLocalizedRouteSegment(locale: Locale, route: RouteKey): string {
  return localizedRoutes[locale][route];
}
